import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { getClientById, getObjectById, subscribeToInspectionsByObject, getInspectionById, deleteInspection, updateInspectionData, subscribeToDDLByDropdownId, subscribeToDDLFullItemsByDropdownId, subscribeToDDLCollections, subscribeToDropdownRegistrations, updateObjectCaptures, subscribeToObjectById, syncCaptures, getSyncStatus, deleteCaptures, getCaptures, getUTData, saveUTData, getDefoResults, deleteDefoResultByKind } from '../utils/storage';
import IMAGE_CODES from '../constants/imageCodes';
import LoadingScreen from './LoadingScreen';
import InspectionWizardPage from './InspectionWizardPage';
import CapturesGallery from './CapturesGallery';
import AppendixCard from './AppendixCard';
import { DefoSubcards } from './DefoResultsCard';
import CreateDefoAnalysisModal, { DEFO_ANALYSES, defoKey, defoAnalysisByKey } from './CreateDefoAnalysisModal';
import ReferenceImagesModal from './ReferenceImagesModal';
import CreateUTTableModal from './CreateUTTableModal';
import SubcardEditModal, { getCounterConfig } from './SubcardEditModal';
import { ReadOnlyContext, useReadOnly } from '../contexts/ReadOnlyContext';
import './ClientDetail.css';
import './ObjectDetail.css';
import './InspectionDetail.css';
import './InspectionStatus.css';

// ── Inspection type display info ───────────────────────────────────

const INSPECTION_TYPE_INFO = {
  'VT-OSI': { label: 'Visual Out of Service Inspection',     category: 'Visual Inspection' },
  'VT-ISI': { label: 'Visual In Service Inspection',         category: 'Visual Inspection' },
  // 'VT' is the umbrella visual inspection — same form / equipment /
  // capture flow as VT-OSI / VT-ISI, just without the in-/out-of-
  // service distinction in the report title.
  'VT':     { label: 'Visual Inspection',                    category: 'Visual Inspection' },
  'UT-OSI': { label: 'Ultrasonic Out of Service Inspection', category: 'Ultrasonic Inspection' },
  'UT-ISI': { label: 'Ultrasonic In Service Inspection',     category: 'Ultrasonic Inspection' },
  'UT':     { label: 'Ultrasonic Inspection',                category: 'Ultrasonic Inspection' },
  'DEFO':   { label: 'Deformation Measurement Analysis',     category: 'Deformation Measurement' },
  'MT-OSI': { label: 'Magnetic Out of Service Inspection',   category: 'Magnetic Inspection' },
  'MT-ISI': { label: 'Magnetic In Service Inspection',       category: 'Magnetic Inspection' },
  'MT':     { label: 'Magnetic Inspection',                  category: 'Magnetic Inspection' },
  'PT-OSI': { label: 'Penetrant Out of Service Inspection',  category: 'Penetrant Inspection' },
  'PT-ISI': { label: 'Penetrant In Service Inspection',      category: 'Penetrant Inspection' },
  'PT':     { label: 'Penetrant Inspection',                 category: 'Penetrant Inspection' },
  'CW-OSI': { label: 'Corner-weld Internal Dimension Inspection', category: 'Corner-weld Inspection' },
};

// ── Wizard config per inspection type ─────────────────────────────
// typePrefix   : namespaces DDL IDs  (e.g. "vtosi_inspectionStatus")
// ndtOperatorCollection : DDL collection name for the operator picker
// defaults     : locked-field fallback values for Procedure / Examination pages

const VT_DEFAULTS = {
  subject:            'Visual Examination',
  specificCode:       'NEN-EN 13018:2016 and ISO 17637:2016',
  acceptanceStandard: 'As per client instructions',
  technique:          'Indirect visual examination',
  extentOfTesting:    'As per inspection plan',
  surfaceTemp:        'Ambient',
};

const UT_DEFAULTS = {
  subject:            'Thickness Measurement Examination',
  specificCode:       'NEN-EN ISO 16809:2019',
  acceptanceStandard: 'As per client instructions',
  technique:          'Contact, echo to echo',
  extentOfTesting:    'As per inspection plan',
  surfaceTemp:        'Ambient',
};

const MT_DEFAULTS = {
  subject:            'Magnetic Particle Examination',
  specificCode:       'NEN-EN ISO 17638:2016',
  acceptanceStandard: 'NEN-EN ISO 23278:2009',
  technique:          'Continuous yoke magnetization',
  extentOfTesting:    'As per inspection plan',
  surfaceTemp:        'Ambient',
};

// PT (Penetrant Testing) — scaffold mirrors MT_DEFAULTS so the wizard
// renders straight away. The user will replace these with the real
// PT-specific values (subject, codes, technique, …) later.
const PT_DEFAULTS = {
  subject:            'Penetrant Examination',
  specificCode:       'NEN-EN ISO 3452-1:2021',
  acceptanceStandard: 'EN 14015:2004/ ISO 6520-1',
  technique:          'Visible colour-contrast',
  extentOfTesting:    'As per inspection plan',
  surfaceTemp:        'Ambient',
};

const DEFO_DEFAULTS = {
  subject:            'Geometric Deformation Analysis',
  specificCode:       'Not applicable',
  acceptanceStandard: 'Not applicable',
  technique:          'Photogrammetry and analysis',
  extentOfTesting:    'As per inspection plan',
  surfaceTemp:        'Ambient',
};

// CW-OSI locked-field semantics: minimal "inspection information +
// appendix" shape, plus a dedicated measurements card. Acceptance Standard
// is locked here (no dropdown in ddlRegistry).
const CW_DEFAULTS = {
  subject:            'Dimension Measurement Examination',
  specificCode:       'Not applicable',
  acceptanceStandard: 'Not applicable',
  technique:          'Direct weld profile',
  extentOfTesting:    'As per inspection plan',
  surfaceTemp:        'Ambient',
};

// equipmentCategory: when set, the wizard's Equipment page shows a multi-select
//   modal listing all equipment in that category (name + expiry badge).
// When null, the wizard falls back to the legacy free-text modal (DEFO only).
const INSPECTION_WIZARD_CONFIG = {
  'VT-OSI': { typePrefix: 'vtosi', ndtOperatorCollection: 'ddl_VTNDTOperator', defaults: VT_DEFAULTS,   equipmentCategory: 'VT Equipment' },
  'VT-ISI': { typePrefix: 'vtisi', ndtOperatorCollection: 'ddl_VTNDTOperator', defaults: VT_DEFAULTS,   equipmentCategory: 'VT Equipment' },
  'VT':     { typePrefix: 'vt',    ndtOperatorCollection: 'ddl_VTNDTOperator', defaults: VT_DEFAULTS,   equipmentCategory: 'VT Equipment' },
  'UT-OSI': { typePrefix: 'utosi', ndtOperatorCollection: 'ddl_UTNDTOperator', defaults: UT_DEFAULTS,   equipmentCategory: 'UT Equipment' },
  'UT-ISI': { typePrefix: 'utisi', ndtOperatorCollection: 'ddl_UTNDTOperator', defaults: UT_DEFAULTS,   equipmentCategory: 'UT Equipment' },
  'UT':     { typePrefix: 'ut',    ndtOperatorCollection: 'ddl_UTNDTOperator', defaults: UT_DEFAULTS,   equipmentCategory: 'UT Equipment' },
  'DEFO':   { typePrefix: 'defo',  ndtOperatorCollection: 'ddl_VTNDTOperator', defaults: DEFO_DEFAULTS, equipmentCategory: null },
  // MT equipment is hard-coded to a 5-piece list in the wizard, so
  // there's no upstream category to pull from. equipmentCategory: null
  // tells the wizard to skip the category-picker modal (we hide the
  // Add button anyway via the isMT branch).
  'MT-OSI': { typePrefix: 'mtosi', ndtOperatorCollection: 'ddl_MTNDTOperator', defaults: MT_DEFAULTS,   equipmentCategory: null },
  'MT-ISI': { typePrefix: 'mtisi', ndtOperatorCollection: 'ddl_MTNDTOperator', defaults: MT_DEFAULTS,   equipmentCategory: null },
  'MT':     { typePrefix: 'mt',    ndtOperatorCollection: 'ddl_MTNDTOperator', defaults: MT_DEFAULTS,   equipmentCategory: null },
  // PT scaffold — replicates MT (locked equipment list, no upstream
  // category to sync). User will tune the defaults / equipment list /
  // examination fields later.
  'PT-OSI': { typePrefix: 'ptosi', ndtOperatorCollection: 'ddl_PTNDTOperator', defaults: PT_DEFAULTS,   equipmentCategory: null },
  'PT-ISI': { typePrefix: 'ptisi', ndtOperatorCollection: 'ddl_PTNDTOperator', defaults: PT_DEFAULTS,   equipmentCategory: null },
  'PT':     { typePrefix: 'pt',    ndtOperatorCollection: 'ddl_PTNDTOperator', defaults: PT_DEFAULTS,   equipmentCategory: null },
  // CW-OSI behaves like DEFO: inspection information + appendix only,
  // no equipment category (free-text), no captures, no measurement sheets.
  'CW-OSI': { typePrefix: 'cwosi', ndtOperatorCollection: 'ddl_VTNDTOperator', defaults: CW_DEFAULTS,   equipmentCategory: null },
};

// Inspection types that have a Captures section
const CAPTURING_TYPES = ['VT-OSI', 'VT-ISI', 'VT', 'MT-OSI', 'MT-ISI', 'MT', 'PT-OSI', 'PT-ISI', 'PT'];

// ── EditOrDisplay: inline editable text area ─────────────────────
// Defined at module level so React never remounts it due to parent re-renders.
// displayValue : the current saved string
// onSave       : async (newValue) => {...}
// required     : if true, shows "Required" hint when empty
// emptyLabel   : text shown when display value is empty (default "None")
function EditOrDisplay({ displayValue, onSave, required, emptyLabel, leftAction }) {
  const readOnly = useReadOnly();   // archived → display only, no Edit
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(displayValue || '');
  const [saving, setSaving]   = useState(false);

  // Keep draft in sync when saved value changes externally — but not while user is editing
  useEffect(() => { if (!editing) setDraft(displayValue || ''); }, [displayValue, editing]);

  async function handleSave() {
    setSaving(true);
    await onSave(draft.trim());
    setSaving(false);
    setEditing(false);
  }

  function handleCancel() {
    setDraft(displayValue || '');
    setEditing(false);
  }

  const lines = (displayValue || '').split('\n').filter((l) => l.trim());
  const isEmpty = lines.length === 0;

  if (editing && !readOnly) {
    return (
      <div className="eod-container">
        <textarea
          className="eod-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={6}
          placeholder="Enter each item on a new line..."
          autoFocus
        />
        <div className={`eod-actions${leftAction ? ' eod-actions-split' : ''}`}>
          <div className="eod-actions-left">{leftAction}</div>
          <div className="eod-actions-right">
            <button className="btn btn-secondary btn-sm" onClick={handleCancel} disabled={saving}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="eod-container">
      <div className={`eod-display${readOnly ? '' : ' eod-display--with-btn'}`}>
        {isEmpty ? (
          <span className={`eod-empty ${required ? 'eod-required' : ''}`}>
            {required ? 'Required — click Edit to fill in' : (emptyLabel || 'None')}
          </span>
        ) : (
          <ul className="insp-synopsis-list">
            {lines.map((line, i) => {
              // High-priority opt-in: any line whose source starts with
              // "* " renders bold WITHOUT a bullet (same font size). The
              // prefix is stripped from the displayed text. PDF builder
              // applies the same rule via _promote_priority_lines.
              const trimmed = line.trim();
              const prio = trimmed.startsWith('* ');
              const text = prio ? trimmed.slice(2).trim() : trimmed;
              return (
                <li key={i} className={prio ? 'prio' : undefined}>{text}</li>
              );
            })}
          </ul>
        )}
      </div>
      {!readOnly && (
        <button className="btn btn-secondary btn-sm eod-edit-btn" onClick={() => setEditing(true)}>Edit</button>
      )}
    </div>
  );
}

// True iff at least one cell in the saved sheets array has a value.
// Handles both `celldata` (sparse list, template path) and `data` (2D
// matrix, FortuneSheet onChange path).
function hasMeasurements(sheets) {
  if (!Array.isArray(sheets) || sheets.length === 0) return false;
  for (const sheet of sheets) {
    const cd = Array.isArray(sheet?.celldata) ? sheet.celldata : null;
    if (cd && cd.some((c) => c && c.v !== undefined && c.v !== null && c.v !== '')) return true;
    const data = Array.isArray(sheet?.data) ? sheet.data : null;
    if (data) {
      for (const row of data) {
        if (!Array.isArray(row)) continue;
        for (const cell of row) {
          if (!cell) continue;
          if (typeof cell === 'object') {
            if (cell.v !== undefined && cell.v !== null && cell.v !== '') return true;
          } else if (cell !== '') {
            return true;
          }
        }
      }
    }
  }
  return false;
}

// Walk every cell across every sheet, applying `fn(cell)`. Stops as soon
// as `fn` returns true. Handles both `celldata` and 2D `data` shapes.
function anyCell(sheets, fn) {
  if (!Array.isArray(sheets)) return false;
  for (const sheet of sheets) {
    const cd = Array.isArray(sheet?.celldata) ? sheet.celldata : null;
    if (cd) {
      for (const c of cd) {
        if (c && c.v && fn(c.v)) return true;
      }
    }
    const data = Array.isArray(sheet?.data) ? sheet.data : null;
    if (data) {
      for (const row of data) {
        if (!Array.isArray(row)) continue;
        for (const cell of row) {
          if (cell && typeof cell === 'object' && fn(cell)) return true;
        }
      }
    }
  }
  return false;
}

// Auto-note 1 trigger: at least one *non-header* cell holds a finite
// number (i.e. measured thickness data, not headings or formulas-only
// cells like a "-" placeholder).
function hasNumericData(sheets) {
  return anyCell(sheets, (v) => {
    if (v.bl === 1 || v.bl === '1' || v.bl === true) return false;
    const fa = v.ct?.fa;
    if (typeof fa === 'string' && fa.indexOf('%') > -1) return false;
    const raw = v.v;
    if (raw === null || raw === undefined || raw === '') return false;
    const num = Number(raw);
    return !Number.isNaN(num) && Number.isFinite(num) && typeof raw !== 'boolean';
  });
}

// Auto-note 2 trigger: any percent-formatted cell holds a negative number.
function hasNegativeDegradation(sheets) {
  return anyCell(sheets, (v) => {
    const fa = v.ct?.fa;
    if (typeof fa !== 'string' || fa.indexOf('%') === -1) return false;
    const raw = v.v;
    if (raw === null || raw === undefined || raw === '') return false;
    const num = Number(raw);
    return !Number.isNaN(num) && Number.isFinite(num) && num < 0;
  });
}

// Auto-note 3 trigger: any bold header cell contains '-R' or '-L'
// (rafter / structural member left/right side measurements).
function hasRLHeaders(sheets) {
  return anyCell(sheets, (v) => {
    if (v.bl !== 1 && v.bl !== '1' && v.bl !== true) return false;
    const text = String(v.v ?? v.m ?? '');
    return text.includes('-R') || text.includes('-L');
  });
}

function autoNotes(sheets) {
  const notes = [];
  if (hasNumericData(sheets)) {
    notes.push('All measurements in the above table are in mm unless stated otherwise.');
  }
  if (hasNegativeDegradation(sheets)) {
    notes.push('Negative degradation values indicate measured thickness exceeds the nominal thickness.');
  }
  if (hasRLHeaders(sheets)) {
    notes.push('Measurements labelled -R (right) and -L (left) are taken from opposite sides of the structural member.');
  }
  return notes;
}

// ── UT Table preview (inline render of sheets data) ─────────────────
// Renders the FortuneSheet `data` saved on a UT subcard as a plain HTML
// table. Cells are classified header vs. data by the `bl` (bold) flag —
// matching how UTTablePage stamps template/manual headers. Numeric data
// cells are rounded to 1 decimal place. The leftmost column is left-aligned
// (location names); all other columns center-aligned (numeric readings).
// Per-analysis fields set in the Edit Analysis modal, stored on each
// form_data.defoAnalyses entry next to kind/standard/collapsed. Read by the
// PDF builder (examiner, manual/drone, reference images, notes).
function pickDefoEditFields(a) {
  const out = {};
  if (a?.examinedBy) out.examinedBy = a.examinedBy;
  if (a?.manualOrDrone) out.manualOrDrone = a.manualOrDrone;
  if (Array.isArray(a?.referenceImageIds) && a.referenceImageIds.length) out.referenceImageIds = a.referenceImageIds;
  if (a?.notes) out.notes = a.notes;
  return out;
}

// Appendix image id → the places it's used as a reference image: the
// synopsis' list of annotations (VT / MT / PT) and each measurement table's
// Reference Images (UT / CW sub-cards). Drives the Appendix thumbnail badge.
function appendixReferences(inspection, utTables) {
  const out = {};
  const add = (id, label) => {
    if (id == null || id === '') return;
    const key = String(id);
    if (!out[key]) out[key] = [];
    if (!out[key].includes(label)) out[key].push(label);
  };
  for (const id of inspection?.formData?.synopsis?.annotationReferenceIds || []) {
    add(id, 'List of annotations');
  }
  for (const t of utTables || []) {
    for (const id of t?.referenceImageIds || []) {
      add(id, `Measurement table: ${t.heading || 'Untitled'}`);
    }
  }
  for (const a of inspection?.formData?.defoAnalyses || []) {
    const title = defoAnalysisByKey(`${a.kind}_${a.standard}`)?.title || 'Deformation analysis';
    for (const id of a?.referenceImageIds || []) add(id, `Analysis: ${title}`);
  }
  return out;
}

// Hand-typed/pasted cells carry only text, while Configure Layout's
// generated cells also carry layout flags. Normalise so both behave the same:
//  - rich-text cells (ct.t === 'inlineStr', text in ct.s[]) get their text
//    surfaced as v/m, so sentinels like "*-" are recognised in them too;
//  - a cell whose text is exactly "⇩" gets the arrow-band flags the
//    generator sets (header-band fill, taller row, no side borders).
// Kept in sync with _normalize_ut_cell in backend/reports/builder.py.
const UT_ARROW = '⇩';
function normalizeUTCell(cell) {
  if (!cell || typeof cell !== 'object') return cell;
  let out = cell;
  const blank = (x) => x === undefined || x === null || x === '';
  if (blank(out.v) && blank(out.m) && out.ct?.t === 'inlineStr' && Array.isArray(out.ct.s)) {
    const text = out.ct.s.map((s) => (s && s.v) || '').join('');
    if (text) out = { ...out, v: text, m: text };
  }
  const raw = out.v ?? out.m;
  if (raw != null && String(raw).trim() === UT_ARROW && !out.headerBg) {
    out = { ...out, headerBg: true, noLeftBorder: true, noRightBorder: true, ht: out.ht ?? 0 };
  }
  return out;
}

function UTTablePreview({ sheets }) {
  if (!Array.isArray(sheets) || sheets.length === 0) return null;
  const sheet = sheets[0];

  // FortuneSheet's onChange saves a 2D matrix in `sheet.data` (cell objects
  // or null), while our template-build path uses `celldata` (sparse list).
  // Build a unified sparse grid from whichever shape is present.
  let maxR = -1;
  let maxC = -1;
  const grid = {};

  const cells = Array.isArray(sheet?.celldata) ? sheet.celldata : [];
  if (cells.length > 0) {
    for (const cell of cells) {
      if (!cell || typeof cell.r !== 'number' || typeof cell.c !== 'number') continue;
      if (cell.v === null || cell.v === undefined) continue;
      if (cell.r > maxR) maxR = cell.r;
      if (cell.c > maxC) maxC = cell.c;
      if (!grid[cell.r]) grid[cell.r] = {};
      grid[cell.r][cell.c] = normalizeUTCell(cell.v);
    }
  } else if (Array.isArray(sheet?.data)) {
    // 2D matrix path: only count cells that actually have a value.
    sheet.data.forEach((row, r) => {
      if (!Array.isArray(row)) return;
      row.forEach((rawCell, c) => {
        const cell = normalizeUTCell(rawCell);
        if (!cell) return;
        const hasValue =
          (typeof cell === 'object' &&
            (cell.v !== undefined && cell.v !== null && cell.v !== '')) ||
          (typeof cell !== 'object' && cell !== '');
        if (!hasValue) return;
        if (r > maxR) maxR = r;
        if (c > maxC) maxC = c;
        if (!grid[r]) grid[r] = {};
        grid[r][c] = cell;
      });
    });
  }

  if (maxR < 0 || maxC < 0) return null;

  // ── Merge handling ──
  // FortuneSheet stores merges on `sheet.config.merge` keyed "r_c" with
  // { r, c, rs, cs } — row/col anchor + row-span + col-span. Build a
  // lookup of "r_c" -> span info, plus a set of covered cells we should
  // skip while rendering (since the anchor cell will rowSpan/colSpan
  // over them).
  const merges = (sheet?.config?.merge && typeof sheet.config.merge === 'object')
    ? sheet.config.merge
    : {};
  const mergeAnchors = {};
  const covered = new Set();
  for (const k of Object.keys(merges)) {
    const m = merges[k];
    if (!m) continue;
    const r0 = m.r ?? 0;
    const c0 = m.c ?? 0;
    const rs = Math.max(1, m.rs ?? 1);
    const cs = Math.max(1, m.cs ?? 1);
    mergeAnchors[`${r0}_${c0}`] = { rs, cs };
    for (let dr = 0; dr < rs; dr++) {
      for (let dc = 0; dc < cs; dc++) {
        if (dr === 0 && dc === 0) continue;
        covered.add(`${r0 + dr}_${c0 + dc}`);
        // Make sure the merged area is included in the rendered grid.
        if (r0 + dr > maxR) maxR = r0 + dr;
        if (c0 + dc > maxC) maxC = c0 + dc;
      }
    }
  }

  // ── '*' merge pre-pass ─────────────────────────────────────────────
  // Walk each column top-to-bottom. Whenever a cell's display value is the
  // sentinel '*', collapse it into the nearest non-empty, non-'*' cell above
  // (the "anchor"), extending its rowSpan. Empty cells break the chain. The
  // sentinel works regardless of cell format — bold/numeric/percent/etc.
  function rawValue(v) {
    if (v === null || v === undefined) return null;
    if (typeof v === 'object') {
      const r = v.v ?? v.m;
      return (r === undefined) ? null : r;
    }
    return v;
  }
  for (let c = 0; c <= maxC; c++) {
    let anchorR = null;
    for (let r = 0; r <= maxR; r++) {
      if (covered.has(`${r}_${c}`)) { anchorR = null; continue; }
      const raw = rawValue(grid[r]?.[c]);
      const sval = (raw === null) ? '' : String(raw).trim();
      if (sval === '*') {
        if (anchorR !== null) {
          const akey = `${anchorR}_${c}`;
          const cur  = mergeAnchors[akey] || { rs: 1, cs: 1 };
          mergeAnchors[akey] = { rs: cur.rs + 1, cs: cur.cs };
          covered.add(`${r}_${c}`);
        }
        // else: stray '*' at top of column — render as-is
      } else {
        // Any non-'*' cell (filled or empty) becomes the next merge anchor.
        // Empty anchors absorb subsequent '*'s (e.g. shared-nominal cells the
        // user fills in over the merged blank); they stay rs=1 if nothing follows.
        anchorR = r;
      }
    }
  }

  // ── Horizontal merge sentinels — row-by-row ────────────────────
  //   `-*` = merge into the cell on the LEFT; `*-` = merge into the cell
  //   on the RIGHT. The neighbour may be empty (→ one wider blank cell),
  //   just like `*` merges into an empty cell above.
  //   Vertical `*` merges have priority — any cell in the proposed
  //   horizontal span that is already vertically covered or vertically
  //   anchored (rs > 1) blocks the merge.
  //   For `*-` right-merges the OOXML/HTML anchor is the leftmost
  //   sentinel cell; we REWRITE its grid entry with the end (anchor)
  //   cell's full payload so value + bold + bg flow through naturally.
  function cellHasValue(val) {
    if (val == null) return false;
    const raw = rawValue(val);
    return raw !== null && raw !== undefined && raw !== '';
  }
  function isHSentinel(val) {
    const raw = rawValue(val);
    if (raw == null) return false;
    const s = String(raw).trim();
    return s === '-*' || s === '*-';
  }
  function isLeftSentinel(val) {
    const raw = rawValue(val);
    return raw != null && String(raw).trim() === '-*';
  }
  function isRightSentinel(val) {
    const raw = rawValue(val);
    return raw != null && String(raw).trim() === '*-';
  }
  function rangeCleanForHMerge(r, cStart, cEnd) {
    for (let cc = cStart; cc <= cEnd; cc++) {
      if (covered.has(`${r}_${cc}`)) return false;
      const anc = mergeAnchors[`${r}_${cc}`];
      if (anc && anc.rs > 1) return false;
    }
    return true;
  }
  for (let r = 0; r <= maxR; r++) {
    let c = 0;
    while (c <= maxC) {
      const v = grid[r]?.[c];
      if (isRightSentinel(v)) {
        let end = c;
        while (end + 1 <= maxC && isRightSentinel(grid[r]?.[end + 1])) end += 1;
        const anchorC = end + 1;
        const anchorV = anchorC <= maxC ? grid[r]?.[anchorC] : null;
        if (
          anchorC <= maxC &&
          !isHSentinel(anchorV) &&
          rangeCleanForHMerge(r, c, anchorC)
        ) {
          const cs = anchorC - c + 1;
          if (!grid[r]) grid[r] = {};
          // Rewrite the OOXML/HTML anchor (leftmost sentinel) to carry the
          // END cell's full payload. An empty end cell leaves the sentinel
          // in place; the blanking pass below empties its text.
          if (cellHasValue(anchorV)) grid[r][c] = anchorV;
          mergeAnchors[`${r}_${c}`] = { rs: 1, cs };
          for (let cc = c + 1; cc <= anchorC; cc++) covered.add(`${r}_${cc}`);
          c = anchorC + 1;
          continue;
        }
        c = end + 1;
        continue;
      }
      if (
        !isHSentinel(v) &&
        c + 1 <= maxC &&
        isLeftSentinel(grid[r]?.[c + 1])
      ) {
        let end = c + 1;
        while (end + 1 <= maxC && isLeftSentinel(grid[r]?.[end + 1])) end += 1;
        if (rangeCleanForHMerge(r, c, end)) {
          const cs = end - c + 1;
          mergeAnchors[`${r}_${c}`] = { rs: 1, cs };
          for (let cc = c + 1; cc <= end; cc++) covered.add(`${r}_${cc}`);
          c = end + 1;
          continue;
        }
      }
      c += 1;
    }
  }

  // A merge symbol that couldn't merge (no neighbour in range, or blocked
  // by a vertical merge) shows as an empty cell — never as literal "*-".
  for (const [rk, row] of Object.entries(grid)) {
    for (const [ck, v] of Object.entries(row)) {
      if (covered.has(`${rk}_${ck}`)) continue;
      const raw = rawValue(v);
      const s = raw == null ? '' : String(raw).trim();
      if (s === '*' || s === '-*' || s === '*-') {
        row[ck] = (v && typeof v === 'object') ? { ...v, v: '', m: '' } : '';
      }
    }
  }

  // Arrow band, decided by what's visible rather than hidden cell flags: a
  // row containing a ⇩ gets the band look across the whole row (fill, taller
  // row, no vertical lines between cells) — blank and merged cells included —
  // however the row was made. "Overall" keeps its own style; the table's
  // outer left/right edges keep their lines. Mirrors builder.py.
  const isText = (v, t) => { const raw = rawValue(v); return raw != null && String(raw).trim() === t; };
  for (let r = 0; r <= maxR; r++) {
    const row = grid[r];
    if (!row || !Object.values(row).some((v) => isText(v, UT_ARROW))) continue;
    for (let c = 0; c <= maxC; c++) {
      if (covered.has(`${r}_${c}`)) continue;
      const v = row[c];
      if (isText(v, 'Overall')) continue;
      const base = (v && typeof v === 'object') ? v : { v: v ?? '', m: v ?? '' };
      const lastC = c + (mergeAnchors[`${r}_${c}`]?.cs ?? 1) - 1;
      row[c] = {
        ...base,
        headerBg: true,
        noLeftBorder: c > 0 || !!base.noLeftBorder,
        noRightBorder: lastC < maxC || !!base.noRightBorder,
      };
    }
  }

  function isPercentCell(v) {
    if (!v || typeof v !== 'object') return false;
    const fa = v.ct?.fa;
    return typeof fa === 'string' && fa.indexOf('%') > -1;
  }

  function format(v) {
    if (v === null || v === undefined) return '';
    if (typeof v === 'object') {
      // Cell value object — pull display text / value out.
      const raw = v.v ?? v.m;
      if (raw === null || raw === undefined || raw === '') return '';
      // Percent-formatted cell: show "X.X%" using the underlying fraction.
      if (isPercentCell(v)) {
        const num = Number(raw);
        if (!Number.isNaN(num) && Number.isFinite(num)) {
          return `${(num * 100).toFixed(1)}%`;
        }
        return String(raw);
      }
      const num = Number(raw);
      if (!Number.isNaN(num) && Number.isFinite(num) && typeof raw !== 'boolean'
          && String(raw).trim() !== '') {
        return num.toFixed(1);
      }
      return String(raw);
    }
    return String(v);
  }

  function isHeader(v) {
    return !!(v && typeof v === 'object' && (v.bl === 1 || v.bl === '1' || v.bl === true));
  }

  // ── Header-row + separator-row detection ────────────────────────────
  // Header rows: consecutive bold-only rows from the top. Falls back to [0].
  // Borderless cells (decorative Overall-column spacers, label-row blanks)
  // are skipped so they don't pull a row out of header classification.
  const headerRows = [];
  for (let r = 0; r <= maxR; r++) {
    let anyData = false;
    let allBold = true;
    for (let c = 0; c <= maxC; c++) {
      if (covered.has(`${r}_${c}`)) continue;
      const v = grid[r]?.[c];
      if (v === undefined || v === null) continue;
      if (v && typeof v === 'object' && v.borderless) continue;
      anyData = true;
      if (!isHeader(v)) { allBold = false; break; }
    }
    if (anyData && allBold) headerRows.push(r);
    else break;
  }
  const lastHeader = headerRows.length > 0 ? headerRows[headerRows.length - 1] : -1;

  // A row is a `--` separator iff strict rules pass (see backend
  // _is_separator_row docstring — kept in sync intentionally).
  function isSeparatorRow(r) {
    const v0 = grid[r]?.[0];
    const raw0 = rawValue(v0);
    if (raw0 === null || raw0 === undefined) return false;
    if (String(raw0).trim() !== '--') return false;
    if (covered.has(`${r}_0`)) return false;
    const anchor0 = mergeAnchors[`${r}_0`];
    if (anchor0 && (anchor0.rs ?? 1) > 1) return false;
    for (let c = 1; c <= maxC; c++) {
      if (covered.has(`${r}_${c}`)) return false;
      const v = grid[r]?.[c];
      if (v === undefined || v === null) continue;
      const raw = rawValue(v);
      if (raw === null || raw === undefined || String(raw).trim() === '') continue;
      return false;
    }
    return true;
  }

  const sepRows = [];
  for (let r = lastHeader + 1; r <= maxR; r++) {
    if (isSeparatorRow(r)) sepRows.push(r);
  }

  // Carve data rows into runs between separators. Each segment renders
  // as its own <table> with the header rows repeated at the top.
  const segments = [];
  {
    let cursor = lastHeader + 1;
    const boundaries = [...sepRows, maxR + 1];
    for (const b of boundaries) {
      if (cursor < b) {
        const run = [];
        for (let r = cursor; r < b; r++) run.push(r);
        if (run.length > 0) segments.push(run);
      }
      cursor = b + 1;
    }
  }
  // If there were no usable separators (or the splits produced fewer
  // than 2 non-empty segments) collapse back to a single segment over
  // all data rows.
  const splitHappened = segments.length >= 2 && sepRows.length > 0;
  const segmentRunLists = splitHappened
    ? segments
    : [(() => {
        const all = [];
        for (let r = lastHeader + 1; r <= maxR; r++) all.push(r);
        return all;
      })()];

  // Structural detection: find any "Overall" cell per segment so cells
  // directly above it (same column) can be treated as borderless white
  // spacers WITHOUT depending on whether the saved data carries the
  // `whiteBg`/`borderless` flags. Works on legacy ut_data too.
  function findOverallAnchors(rowList) {
    const anchors = [];
    for (const r of rowList) {
      for (let c = 0; c <= maxC; c++) {
        const cv = grid[r]?.[c];
        const txt = cv && typeof cv === 'object'
          ? String(cv.m ?? cv.v ?? '')
          : (cv == null ? '' : String(cv));
        if (txt.trim() === 'Overall') {
          anchors.push({ r, c });
        }
      }
    }
    return anchors;
  }

  function renderCell(r, c, segCtx) {
    if (covered.has(`${r}_${c}`)) return null;
    const v = grid[r]?.[c];
    const anchor = mergeAnchors[`${r}_${c}`];

    // Structural Overall-spacer override: cells directly above an
    // "Overall" anchor cell (same column, rows ABOVE) → forced
    // borderless + white bg.
    const isOverallSpacer = segCtx?.overallAnchors?.some(
      a => a.c === c && r < a.r
    );
    const isOverallCell = segCtx?.overallAnchors?.some(
      a => a.c === c && a.r === r
    );

    const header = isHeader(v) && !isOverallSpacer;
    const text = header
      ? (v && typeof v === 'object' ? String(v.m ?? v.v ?? '') : String(v ?? ''))
      : (isOverallSpacer ? '' : format(v));
    // Cell-level horizontal alignment via FortuneSheet `ht` field
    // (0 = center, 1 = left, 2 = right). Falls back to "left for
    // col 0, center otherwise".
    const ht = (v && typeof v === 'object') ? v.ht : undefined;
    const align =
      ht === 1 ? 'left' :
      ht === 2 ? 'right' :
      ht === 0 ? 'center' :
      (c === 0 ? 'left' : 'center');
    const hdrBg = (v && typeof v === 'object' && v.headerBg);
    const whiteBg = (v && typeof v === 'object' && v.whiteBg) || isOverallSpacer;
    const cls = `insp-ut-preview-cell${header ? ' header' : ''}${hdrBg ? ' header-bg' : ''}${whiteBg ? ' white-bg' : ''}`;
    // Cell background colour passthrough — FortuneSheet stores it as
    // cell.bg = "#RRGGBB". For horizontal-merge anchors the grid entry
    // was already rewritten to the END cell's payload above, so the
    // colour flows through merges naturally.
    const bg = (v && typeof v === 'object' ? v.bg : null);
    const borderless = (v && typeof v === 'object' && v.borderless) || isOverallSpacer || isOverallCell;
    const style = { textAlign: align };
    if (typeof bg === 'string' && bg.trim() && !isOverallSpacer) style.backgroundColor = bg;
    if (borderless) {
      style.border = 'none';
    } else if (v && typeof v === 'object') {
      if (v.noLeftBorder)   style.borderLeft   = 'none';
      if (v.noRightBorder)  style.borderRight  = 'none';
      if (v.noTopBorder)    style.borderTop    = 'none';
      if (v.noBottomBorder) style.borderBottom = 'none';
    }

    // Web-only outer-edge stripping. The PDF keeps its outer frame; the
    // web preview drops the table's outermost edges so the table reads
    // as content rather than a contained box. Corner cells naturally
    // lose two edges. Applied AFTER per-cell border flags so it always
    // wins for edge cells.
    if (segCtx) {
      if (r === segCtx.topR)    style.borderTop    = 'none';
      if (r === segCtx.bottomR) style.borderBottom = 'none';
      if (c === 0)              style.borderLeft   = 'none';
      if (c === maxC)           style.borderRight  = 'none';
    }

    return (
      <td
        key={c}
        className={cls}
        style={style}
        rowSpan={anchor?.rs > 1 ? anchor.rs : undefined}
        colSpan={anchor?.cs > 1 ? anchor.cs : undefined}
      >
        {text}
      </td>
    );
  }

  // ── Shared column widths across split sub-tables ───────────────────
  // Each segment is its own <table>; with auto layout their columns size to
  // their own content, so vertical borders don't line up between sub-tables.
  // Size every column by the widest display text anywhere in that column
  // (single-column cells only), then pin the SAME proportional width to that
  // column in every segment via a shared <colgroup> + table-layout: fixed.
  // Same column index → same width across sub-tables; different columns keep
  // their own width. (Web preview only; the PDF/DOCX tables are unaffected.)
  const colChars = new Array(maxC + 1).fill(0);
  for (let r = 0; r <= maxR; r++) {
    for (let c = 0; c <= maxC; c++) {
      if (covered.has(`${r}_${c}`)) continue;
      const anchor = mergeAnchors[`${r}_${c}`];
      if (anchor && anchor.cs > 1) continue;   // spans columns — don't size one
      const v = grid[r]?.[c];
      if (v === undefined || v === null) continue;
      const txt = isHeader(v)
        ? (typeof v === 'object' ? String(v.m ?? v.v ?? '') : String(v))
        : format(v);
      const len = (txt || '').length;
      if (len > colChars[c]) colChars[c] = len;
    }
  }
  const colWeights = colChars.map((n) => Math.max(3, n + 2.5));
  const totalWeight = colWeights.reduce((a, b) => a + b, 0) || 1;
  const colPct = colWeights.map((w) => `${(w / totalWeight) * 100}%`);

  function renderSegment(dataRows, segIdx) {
    // The order is: header rows first, then this segment's data rows.
    // We render header cells with their ORIGINAL row index (so merges
    // anchored in the header still work). For data rows the same —
    // merges across data rows in the original sheet were already kept
    // intact (they're confined within this segment by construction).
    const ordered = [...headerRows, ...dataRows];
    const segCtx = {
      topR:    ordered[0],
      bottomR: ordered[ordered.length - 1],
      overallAnchors: findOverallAnchors(ordered),
    };
    return (
      <div
        key={segIdx}
        className="insp-ut-preview-wrap"
        style={{ marginTop: segIdx > 0 ? '0.75rem' : undefined }}
      >
        <table className="insp-ut-preview-table" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            {colPct.map((w, c) => <col key={c} style={{ width: w }} />)}
          </colgroup>
          <tbody>
            {ordered.map((origR, idx) => {
              // Separator/arrow row gets +30% height: any cell with headerBg
              // (arrow band) flags the row.
              const isSep = Array.from({ length: maxC + 1 }, (_, c) => {
                const cv = grid[origR]?.[c];
                return cv && typeof cv === 'object' && cv.headerBg;
              }).some(Boolean);
              return (
                <tr
                  key={`${segIdx}_${idx}_${origR}`}
                  className={isSep ? 'insp-ut-preview-sep-row' : undefined}
                >
                  {Array.from({ length: maxC + 1 }, (_, c) => renderCell(origR, c, segCtx))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return <>{segmentRunLists.map((dataRows, i) => renderSegment(dataRows, i))}</>;
}

// ── Component ──────────────────────────────────────────────────────

export default function InspectionDetail() {
  const { id: clientId, objectId, inspectionId } = useParams();
  const navigate = useNavigate();
  const { setBreadcrumb, clearBreadcrumb } = useBreadcrumb();
  const [client,            setClient]            = useState(null);
  const [object,            setObject]            = useState(null);
  const [inspection,        setInspection]        = useState(null);
  const [allInspections,    setAllInspections]    = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [pageReady,         setPageReady]         = useState(false);
  const [showEditModal,     setShowEditModal]     = useState(false);
  const [capturesDrivePath,     setCapturesDrivePath]     = useState('');
  const [capturesObjectType,    setCapturesObjectType]    = useState('');
  const [capturesComposition,   setCapturesComposition]   = useState('');
  const [objectTypeOpts,        setObjectTypeOpts]        = useState([]);
  const [compositionFullItems,  setCompositionFullItems]  = useState([]);
  const [ddlRegConfigs,         setDdlRegConfigs]         = useState({});
  // Collapsible section state — persisted per session via sessionStorage
  const [synopsisOpen, setSynopsisOpen] = useState(() => {
    const stored = sessionStorage.getItem(`tib_collapse_synopsis_${inspectionId}`);
    return stored !== null ? stored === 'true' : true;
  });
  const [magSynopsisOpen, setMagSynopsisOpen] = useState(() => {
    const stored = sessionStorage.getItem(`tib_collapse_magsynopsis_${inspectionId}`);
    return stored !== null ? stored === 'true' : true;
  });
  const [ptSynopsisOpen, setPtSynopsisOpen] = useState(() => {
    const stored = sessionStorage.getItem(`tib_collapse_ptsynopsis_${inspectionId}`);
    return stored !== null ? stored === 'true' : true;
  });
  const [capturesOpen, setCapturesOpen] = useState(() => {
    const stored = sessionStorage.getItem(`tib_collapse_captures_${inspectionId}`);
    return stored !== null ? stored === 'true' : true;
  });
  const [utCardOpen, setUtCardOpen] = useState(() => {
    const stored = sessionStorage.getItem(`tib_collapse_ut_${inspectionId}`);
    return stored !== null ? stored === 'true' : true;
  });
  // Deformation Analysis card (DEFO only). Mirrors the UT measurements card:
  // one main card holding analysis sub-cards. `defoAnalyses` is the ordered
  // list of added analyses ([{ kind, standard, collapsed }]), persisted in
  // form_data.defoAnalyses.
  const [defoCardOpen, setDefoCardOpen] = useState(() => {
    const stored = sessionStorage.getItem(`tib_collapse_defo_${inspectionId}`);
    return stored !== null ? stored === 'true' : true;
  });
  const [defoAnalyses, setDefoAnalyses] = useState([]);
  const [showCreateDefoModal, setShowCreateDefoModal] = useState(false);
  const [defoDeleteKey, setDefoDeleteKey] = useState(null);     // "kind_standard" pending delete
  const [defoEditKey, setDefoEditKey] = useState(null);         // "kind_standard" open in Edit Analysis
  const [defoReorder, setDefoReorder] = useState(null);         // { key, prevCollapse } | null
  const defoReorderTimerRef = useRef(null);
  const defoSeededRef = useRef(false);
  // Which analysis kinds already have saved results — drives the sub-card
  // button label ("Redo Analysis" vs "Run Analysis").
  const [defoResultKinds, setDefoResultKinds] = useState(() => new Set());
  // UT measurement tables: [{id, component, measurement, heading, originalHeading, collapsed, sheets}]
  const [utTables, setUtTables] = useState([]);
  const [showCreateUtModal, setShowCreateUtModal] = useState(false);
  const [utDeleteId, setUtDeleteId] = useState(null);          // tableId pending delete confirmation
  const [utEditId, setUtEditId] = useState(null);               // tableId being edited
  const [utReorder, setUtReorder] = useState(null);             // { tableId, prevCollapse: { id: bool } } | null
  const utReorderTimerRef = useRef(null);
  // Tracks the user's last bulk-toggle action so the "Expand/Collapse all"
  // button label persists through middle states (some collapsed, some
  // expanded). When detection reveals all-collapsed or all-expanded the
  // label snaps to the opposite to invite the obvious next action.
  const [utCollapseMode, setUtCollapseMode] = useState('expanded');
  // DDL data lifted up to InspectionDetail so the Create modal opens instantly
  // (no per-modal subscription = no loading flash, no extra round-trip on click).
  const [utDdlCollections, setUtDdlCollections] = useState([]);
  const [syncRunning,   setSyncRunning]   = useState(false);
  const [syncStatus,    setSyncStatus]    = useState(null);   // { phase, total, loaded, failed, first_failed, error }
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [captures,      setCaptures]      = useState([]);
  const pollRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [capturesLoaded, setCapturesLoaded] = useState(false);
  const [showRefImagesModal, setShowRefImagesModal] = useState(false);

  // Persist collapsible state to sessionStorage
  useEffect(() => { sessionStorage.setItem(`tib_collapse_synopsis_${inspectionId}`, synopsisOpen); }, [synopsisOpen, inspectionId]);
  useEffect(() => { sessionStorage.setItem(`tib_collapse_magsynopsis_${inspectionId}`, magSynopsisOpen); }, [magSynopsisOpen, inspectionId]);
  useEffect(() => { sessionStorage.setItem(`tib_collapse_ptsynopsis_${inspectionId}`, ptSynopsisOpen); }, [ptSynopsisOpen, inspectionId]);
  useEffect(() => { sessionStorage.setItem(`tib_collapse_captures_${inspectionId}`, capturesOpen); }, [capturesOpen, inspectionId]);
  useEffect(() => { sessionStorage.setItem(`tib_collapse_ut_${inspectionId}`, utCardOpen); }, [utCardOpen, inspectionId]);
  useEffect(() => { sessionStorage.setItem(`tib_collapse_defo_${inspectionId}`, defoCardOpen); }, [defoCardOpen, inspectionId]);

  // Immediately re-fetch inspection from API (don't wait for poll)
  async function refreshInspection() {
    try {
      const i = await getInspectionById(inspectionId);
      if (i) setInspection(i);
    } catch (err) {
      console.error('Failed to refresh inspection:', err);
    }
  }

  // Load client, object, and inspection
  useEffect(() => {
    async function load() {
      const [c, o, i] = await Promise.all([
        getClientById(clientId),
        getObjectById(objectId),
        getInspectionById(inspectionId),
      ]);
      if (!c || !o || !i) {
        navigate(`/client/${clientId}/object/${objectId}`);
        return;
      }
      setClient(c);
      setObject(o);
      setInspection(i);
      setLoading(false);
    }
    load();
  }, [clientId, objectId, inspectionId, navigate]);

  // Real-time listener: keeps formData in sync after wizard saves
  useEffect(() => {
    const unsubscribe = subscribeToInspectionsByObject(objectId, (inspectionList) => {
      setAllInspections(inspectionList);
      const current = inspectionList.find((i) => i.id === inspectionId);
      if (current) setInspection(current);
    });
    return () => unsubscribe();
  }, [objectId, inspectionId]);

  // Real-time object subscription for shared captures data
  useEffect(() => {
    const unsubscribe = subscribeToObjectById(objectId, (obj) => setObject(obj));
    return () => unsubscribe();
  }, [objectId]);

  // Breadcrumb: TestSite > ObjectTag > InspectionType
  useEffect(() => {
    if (client && object && inspection) {
      setBreadcrumb([
        { label: client.name, path: `/client/${clientId}` },
        { label: object.objectTag, path: `/client/${clientId}/object/${objectId}` },
        { label: inspection.type, path: null },
      ]);
    }
    return () => clearBreadcrumb();
  }, [client, object, inspection, clientId, objectId, setBreadcrumb, clearBreadcrumb]);

  // ── Synopsis save helper (must be before early return) ──────────
  // For the three long-text fields (inspectedAreas, remarks, annotations) we
  // strip blank lines and collapse runs of spaces/tabs down to one — keeps
  // the PDF output tidy without the user having to hand-format.
  const saveSynopsisField = useCallback(async (fieldName, value) => {
    if (!inspection) return;
    const LONG_TEXT_FIELDS = ['inspectedAreas', 'remarks', 'annotations'];
    let cleaned = value;
    if (typeof value === 'string' && LONG_TEXT_FIELDS.includes(fieldName)) {
      cleaned = value
        .split(/\r?\n/)
        .map((line) => line.replace(/[ \t]+/g, ' ').trim())
        .filter((line) => line.length > 0)
        .join('\n');
    }
    const fd = inspection.formData || {};
    const synopsis = fd.synopsis || {};
    const newFormData = { ...fd, synopsis: { ...synopsis, [fieldName]: cleaned } };
    await updateInspectionData(inspectionId, { formData: newFormData });
    // Optimistic update so the UI reflects the save immediately without waiting for poll
    setInspection((prev) => prev ? { ...prev, formData: newFormData } : prev);
  }, [inspection, inspectionId]);

  // ── Captures helpers (must be before early return) ───────────────
  // Sync drive path from inspection formData when the inspection ID changes
  // (initial load or chevron navigation). Uses inspection.id to detect when
  // the actual loaded inspection matches the URL param, avoiding stale reads.
  const drivePathInitRef = useRef(null);
  useEffect(() => {
    if (inspection && inspection.id === inspectionId && drivePathInitRef.current !== inspectionId) {
      drivePathInitRef.current = inspectionId;
      setCapturesDrivePath(inspection.formData?.capturesDrivePath || '');
    }
  }, [inspection, inspectionId]);

  // Sync object-level captures (shared across all inspections of the object)
  useEffect(() => {
    if (object) {
      setCapturesObjectType(object.capturesObjectType || inspection?.formData?.capturesObjectType || '');
      setCapturesComposition(object.capturesComposition || inspection?.formData?.capturesComposition || '');
    }
  }, [object, inspection]);

  // DDL subscriptions for captures dropdowns
  useEffect(() => subscribeToDDLByDropdownId('captures_inspectObjectType', setObjectTypeOpts), []);
  useEffect(() => subscribeToDDLFullItemsByDropdownId('captures_inspectComposition', setCompositionFullItems), []);
  useEffect(() => subscribeToDropdownRegistrations(setDdlRegConfigs), []);

  // DDL collections subscription — needed by the UT Create-Table modal so it
  // opens with data already loaded (no per-open fetch).
  useEffect(() => subscribeToDDLCollections(setUtDdlCollections), []);

  // Load existing captures from database (VT/MT types only; reset on inspection change)
  useEffect(() => {
    if (!inspectionId || !inspection?.type) return;
    setCaptures([]);
    setCapturesLoaded(false);
    if (!CAPTURING_TYPES.includes(inspection.type)) return;
    let cancelled = false;
    getCaptures(inspectionId).then((data) => {
      if (!cancelled) {
        setCaptures(data);
        setCapturesLoaded(true);
      }
    }).catch(() => {
      if (!cancelled) setCapturesLoaded(true);
    });
    return () => { cancelled = true; };
  }, [inspectionId, inspection?.type]);

  // Start polling for sync status; returns cleanup function
  const startPolling = useCallback((id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    setSyncRunning(true);

    pollRef.current = setInterval(async () => {
      try {
        const status = await getSyncStatus(id);
        setSyncStatus(status);

        if (status.phase === 'done' || status.phase === 'error' || status.phase === 'idle') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setSyncRunning(false);

          // Refresh gallery with now-loaded images
          const fresh = await getCaptures(id);
          setCaptures(fresh);

          // Show completion modal (not for idle — that means no job existed)
          if (status.phase !== 'idle') {
            setShowSyncModal(true);
          }
        }
      } catch {
        // Polling error — keep trying
      }
    }, 2000);
  }, []);

  // On mount: check if a sync is already in progress (VT/MT types only)
  useEffect(() => {
    if (!inspectionId || !inspection?.type) return;
    const cleanup = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setSyncRunning(false);
      setSyncStatus(null);
    };
    if (!CAPTURING_TYPES.includes(inspection.type)) return cleanup;
    let cancelled = false;
    getSyncStatus(inspectionId).then((status) => {
      if (cancelled) return;
      if (status.phase === 'images') {
        setSyncStatus(status);
        startPolling(inspectionId);
      }
    }).catch(() => {});
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [inspectionId, inspection?.type, startPolling]);

  // ── UT Equipment metadata lookup (UT inspections only) ────────
  // Pulls the name → acceptsModule map for the info card's equipment
  // string and the incomplete-info warning. Refetched whenever the
  // inspection type changes (e.g. between tabs).
  const [utEquipMetaByName, setUtEquipMetaByName] = useState({});
  useEffect(() => {
    setUtEquipMetaByName({});
    if (!['UT-OSI', 'UT-ISI', 'UT'].includes(inspection?.type)) return;
    let cancelled = false;
    (async () => {
      try {
        const { get } = await import('../api');
        const [listResp, metaResp] = await Promise.all([
          get('/equipment/by-category/UT%20Equipment'),
          get('/equipment/by-category/UT%20Equipment/metadata'),
        ]);
        if (cancelled) return;
        const items = Array.isArray(listResp?.items) ? listResp.items : [];
        const metaMap = metaResp?.metadata || {};
        const byName = {};
        for (const it of items) {
          const m = metaMap[it.id];
          if (m && it.name) {
            let am = m.acceptsModule;
            if (am === true)  am = 'yes';
            if (am === false) am = 'no';
            byName[it.name] = am;
          }
        }
        setUtEquipMetaByName(byName);
      } catch {
        if (!cancelled) setUtEquipMetaByName({});
      }
    })();
    return () => { cancelled = true; };
  }, [inspection?.type]);

  function equipmentAcceptsModule(name) {
    return (utEquipMetaByName[name] || '').toLowerCase() === 'yes';
  }

  // ── UT measurement tables: load + persist helpers ─────────────
  // Load existing tables from inspection.ut_data (UT-OSI / UT-ISI / CW-OSI).
  // Old shape (a raw FortuneSheet array) is treated as no tables — those
  // were dev-only and the user has confirmed that's okay.
  //
  // CW-OSI has a single fixed sub-card. If none exists yet on this
  // inspection, we seed one synchronously here ("Visual Measurement of
  // Internal Bottom Corner-weld") and persist it so the user lands on a
  // ready-to-edit card without an explicit Create step.
  useEffect(() => {
    if (!inspectionId || !inspection?.type) return;
    setUtTables([]);
    const isMeasInsp = ['UT-OSI', 'UT-ISI', 'UT', 'CW-OSI'].includes(inspection.type);
    if (!isMeasInsp) return;
    let cancelled = false;
    getUTData(inspectionId)
      .then((res) => {
        if (cancelled) return;
        const data = res?.ut_data;
        if (data && Array.isArray(data.tables) && data.tables.length > 0) {
          setUtTables(data.tables);
          return;
        }
        if (inspection.type === 'CW-OSI') {
          // Seed the single default sub-card. Uses crypto.randomUUID when
          // available; falls back to a Math.random id for safety.
          const newId = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `cw-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
          const seed = [{
            id: newId,
            heading: 'Visual Measurement of Internal Bottom Corner-weld',
            originalHeading: 'Visual Measurement of Internal Bottom Corner-weld',
            component: 'Floor (Internal)',
            measurement: 'Bottom corner weld',
            sheets: [],
            collapsed: false,
            scanlineCount: 8,
          }];
          setUtTables(seed);
          saveUTData(inspectionId, { tables: seed }).catch(() => {});
        } else {
          setUtTables([]);
        }
      })
      .catch(() => { if (!cancelled) setUtTables([]); });
    return () => { cancelled = true; };
  }, [inspectionId, inspection?.type]);

  // Listen for save notifications from the UT spreadsheet tab. When the
  // user clicks Save in the UT page, that tab posts a message to its
  // opener (this tab) and then closes itself. We refetch ut_data so the
  // sub-card updates immediately with the saved measurements.
  useEffect(() => {
    if (!inspectionId) return;
    function handleMessage(event) {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.type !== 'tib:ut-saved') return;
      if (data.inspectionId !== inspectionId) return;
      // Refetch the inspection's ut_data so the sub-card preview rerenders.
      getUTData(inspectionId)
        .then((res) => {
          const d = res?.ut_data;
          if (d && Array.isArray(d.tables)) setUtTables(d.tables);
        })
        .catch(() => {});
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [inspectionId]);

  // Persist the full table array to the backend.
  const persistUtTables = useCallback(async (newTables) => {
    setUtTables(newTables);
    try {
      await saveUTData(inspectionId, { tables: newTables });
    } catch (err) {
      console.error('Failed to save UT tables:', err);
    }
  }, [inspectionId]);

  // Create one or more new tables at once (the modal returns an array of
  // {component, measurement, heading, originalHeading} entries).
  async function handleCreateUtTables(items) {
    if (!Array.isArray(items) || items.length === 0) {
      setShowCreateUtModal(false);
      return;
    }
    const newTables = items.map((it) => {
      // Seed scanline/position count from the layout default. The user can
      // override it later in the Edit Measurement Table modal.
      const cfg = getCounterConfig(it.component, it.measurement);
      return {
        id: (window.crypto?.randomUUID?.() || `ut_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`),
        component: it.component,
        measurement: it.measurement,
        heading: it.heading,
        originalHeading: it.originalHeading,
        collapsed: false,
        sheets: null,
        scanlineCount: cfg?.mode === 'scanlines' ? cfg.default : null,
        positionCount: cfg?.mode === 'positions' ? cfg.default : null,
      };
    });
    await persistUtTables([...utTables, ...newTables]);
    setShowCreateUtModal(false);
  }

  async function handleDeleteUtTable(tableId) {
    await persistUtTables(utTables.filter((t) => t.id !== tableId));
    setUtDeleteId(null);
    setUtEditId(null);
  }

  // Patch a table with arbitrary fields (heading, examinedBy, manualOrDrone,
  // referenceImageIds, …) — used by the per-subcard edit modal.
  async function handlePatchUtTable(tableId, patch) {
    await persistUtTables(utTables.map((t) =>
      t.id === tableId ? { ...t, ...patch } : t,
    ));
  }

  async function toggleUtSubcardCollapse(tableId) {
    await persistUtTables(utTables.map((t) =>
      t.id === tableId ? { ...t, collapsed: !t.collapsed } : t,
    ));
  }

  async function toggleUtCollapseAll(targetCollapsed) {
    setUtCollapseMode(targetCollapsed ? 'collapsed' : 'expanded');
    await persistUtTables(utTables.map((t) => ({ ...t, collapsed: targetCollapsed })));
  }

  // ── Long-press reorder ─────────────────────────────────────────
  // Hold the heading for 3s → enter reorder mode: collapse all sub-cards
  // (remember their previous open state), the held card "follows" the
  // mouse via a hovered-index tracker. On mouseup, drop at the hovered
  // index and restore previous collapse states. Cancel on early mouseup.
  function startReorderHold(tableId) {
    if (utReorderTimerRef.current) clearTimeout(utReorderTimerRef.current);
    utReorderTimerRef.current = setTimeout(() => {
      // Snapshot previous collapse states
      const prevCollapse = Object.fromEntries(utTables.map((t) => [t.id, !!t.collapsed]));
      // Collapse all in local state (don't persist — restore on drop)
      setUtTables((cur) => cur.map((t) => ({ ...t, collapsed: true })));
      setUtReorder({ tableId, prevCollapse });
      utReorderTimerRef.current = null;
    }, 500);
  }

  function cancelReorderHold() {
    if (utReorderTimerRef.current) {
      clearTimeout(utReorderTimerRef.current);
      utReorderTimerRef.current = null;
    }
  }

  // Mouse handlers active only while in reorder mode
  useEffect(() => {
    if (!utReorder) return;
    const handleMove = (e) => {
      const cards = document.querySelectorAll('[data-ut-subcard-id]');
      let hoverIdx = -1;
      cards.forEach((card, i) => {
        const r = card.getBoundingClientRect();
        if (e.clientY >= r.top && e.clientY <= r.bottom) hoverIdx = i;
      });
      if (hoverIdx === -1) return;
      // Live reorder: move the held card to hoverIdx
      setUtTables((cur) => {
        const sourceIdx = cur.findIndex((t) => t.id === utReorder.tableId);
        if (sourceIdx === -1 || sourceIdx === hoverIdx) return cur;
        const next = [...cur];
        const [moved] = next.splice(sourceIdx, 1);
        next.splice(hoverIdx, 0, moved);
        return next;
      });
    };
    const handleUp = async () => {
      // Restore previous collapse states + persist final order
      setUtReorder(null);
      setUtTables((cur) => {
        const restored = cur.map((t) => ({ ...t, collapsed: !!utReorder.prevCollapse[t.id] }));
        // Persist asynchronously
        saveUTData(inspectionId, { tables: restored }).catch((err) =>
          console.error('Failed to persist UT table reorder:', err),
        );
        return restored;
      });
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [utReorder, inspectionId]);

  // ── Deformation Analysis: load / seed the analysis sub-card list ───
  // Source of truth is form_data.defoAnalyses. For DEFO inspections created
  // before this card existed (results saved, no list), seed the list from
  // whatever results already exist so nothing is lost.
  useEffect(() => {
    if (!inspection || inspection.type !== 'DEFO' || defoSeededRef.current) return;
    defoSeededRef.current = true;
    const stored = Array.isArray(inspection.formData?.defoAnalyses)
      ? inspection.formData.defoAnalyses
      : null;
    if (stored && stored.length > 0) {
      // Keep only analyses we still recognise, in stored order.
      const valid = stored
        .filter((a) => defoAnalysisByKey(`${a.kind}_${a.standard}`))
        .map((a) => ({ ...pickDefoEditFields(a), kind: a.kind, standard: a.standard, collapsed: !!a.collapsed }));
      setDefoAnalyses(valid);
      return;
    }
    // No stored list — seed from existing results (migration).
    let cancelled = false;
    getDefoResults(inspectionId).then((rows) => {
      if (cancelled) return;
      const present = new Set((rows || []).map((r) => `${r.kind}_${r.standard}`));
      const seed = DEFO_ANALYSES
        .filter((a) => present.has(defoKey(a)))
        .map((a) => ({ kind: a.kind, standard: a.standard, collapsed: false }));
      if (seed.length > 0) persistDefoAnalyses(seed);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspection, inspectionId]);

  // Track which analysis kinds have results, refreshing when an analysis tab
  // saves (BroadcastChannel) or this tab regains focus. Keyed on the inspection
  // TYPE (stable across polls) so it doesn't re-subscribe on every poll tick.
  const inspTypeForDefo = inspection?.type;
  useEffect(() => {
    if (inspTypeForDefo !== 'DEFO') return undefined;
    let cancelled = false;
    const refresh = () => getDefoResults(inspectionId).then((rows) => {
      if (!cancelled) setDefoResultKinds(new Set((rows || []).map((r) => r.kind)));
    });
    refresh();
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    let ch;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        ch = new BroadcastChannel('tib-defo-results');
        ch.onmessage = () => refresh();
      }
    } catch { /* best-effort */ }
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      if (ch) ch.close();
    };
  }, [inspTypeForDefo, inspectionId]);

  // Persist the analysis list to form_data (merging so other form_data keys
  // survive) and keep local inspection state in sync so later form_data saves
  // don't clobber it.
  async function persistDefoAnalyses(list) {
    setDefoAnalyses(list);
    const fd = {
      ...(inspection?.formData || {}),
      defoAnalyses: list.map((a) => ({
        kind: a.kind, standard: a.standard, collapsed: !!a.collapsed, ...pickDefoEditFields(a),
      })),
    };
    setInspection((prev) => (prev ? { ...prev, formData: fd } : prev));
    try {
      await updateInspectionData(inspectionId, { formData: fd });
    } catch (err) {
      console.error('Failed to persist deformation analyses:', err);
    }
  }

  function handleCreateDefoAnalyses(picks) {
    const existing = new Set(defoAnalyses.map((a) => `${a.kind}_${a.standard}`));
    const added = picks
      .filter((p) => !existing.has(`${p.kind}_${p.standard}`))
      .map((p) => ({ kind: p.kind, standard: p.standard, collapsed: false }));
    if (added.length > 0) persistDefoAnalyses([...defoAnalyses, ...added]);
    setShowCreateDefoModal(false);
  }

  async function handleDeleteDefoAnalysis(key) {
    const target = defoAnalyses.find((a) => `${a.kind}_${a.standard}` === key);
    setDefoDeleteKey(null);
    if (!target) return;
    persistDefoAnalyses(defoAnalyses.filter((a) => `${a.kind}_${a.standard}` !== key));
    // Also drop the saved results (+ images) for that analysis.
    try { await deleteDefoResultByKind(inspectionId, target.kind, target.standard); }
    catch (err) { console.error('Failed to delete deformation results:', err); }
  }

  function toggleDefoSubcardCollapse(key) {
    persistDefoAnalyses(defoAnalyses.map((a) =>
      (`${a.kind}_${a.standard}` === key ? { ...a, collapsed: !a.collapsed } : a)));
  }

  // ── Deformation sub-card press-and-hold drag reorder (mirrors UT) ──
  function startDefoReorderHold(key) {
    if (defoReorderTimerRef.current) clearTimeout(defoReorderTimerRef.current);
    defoReorderTimerRef.current = setTimeout(() => {
      const prevCollapse = Object.fromEntries(defoAnalyses.map((a) => [`${a.kind}_${a.standard}`, !!a.collapsed]));
      setDefoAnalyses((cur) => cur.map((a) => ({ ...a, collapsed: true })));
      setDefoReorder({ key, prevCollapse });
      defoReorderTimerRef.current = null;
    }, 500);
  }
  function cancelDefoReorderHold() {
    if (defoReorderTimerRef.current) {
      clearTimeout(defoReorderTimerRef.current);
      defoReorderTimerRef.current = null;
    }
  }
  useEffect(() => {
    if (!defoReorder) return undefined;
    const handleMove = (e) => {
      const cards = document.querySelectorAll('[data-defo-subcard-id]');
      let hoverIdx = -1;
      cards.forEach((card, i) => {
        const r = card.getBoundingClientRect();
        if (e.clientY >= r.top && e.clientY <= r.bottom) hoverIdx = i;
      });
      if (hoverIdx === -1) return;
      setDefoAnalyses((cur) => {
        const sourceIdx = cur.findIndex((a) => `${a.kind}_${a.standard}` === defoReorder.key);
        if (sourceIdx === -1 || sourceIdx === hoverIdx) return cur;
        const next = [...cur];
        const [moved] = next.splice(sourceIdx, 1);
        next.splice(hoverIdx, 0, moved);
        return next;
      });
    };
    const handleUp = () => {
      setDefoReorder(null);
      setDefoAnalyses((cur) => {
        const restored = cur.map((a) => ({ ...a, collapsed: !!defoReorder.prevCollapse[`${a.kind}_${a.standard}`] }));
        persistDefoAnalyses(restored);
        return restored;
      });
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defoReorder, inspectionId]);

  // Save drive path per-inspection
  const saveCapturesDrivePath = useCallback(async (drivePath) => {
    if (!inspection) return;
    const fd = inspection.formData || {};
    await updateInspectionData(inspectionId, {
      formData: { ...fd, capturesDrivePath: drivePath },
    });
  }, [inspection, inspectionId]);

  // Save dropdown values at object level (shared across inspections)
  const saveCapturesDropdowns = useCallback(async (updates) => {
    if (!object) return;
    await updateObjectCaptures(objectId, {
      capturesObjectType: updates.capturesObjectType ?? capturesObjectType,
      capturesComposition: updates.capturesComposition ?? capturesComposition,
    });
  }, [object, objectId, capturesObjectType, capturesComposition]);

  if (!pageReady) {
    return (
      <LoadingScreen
        isLoading={loading || !client || !object || !inspection}
        onDone={() => setPageReady(true)}
      />
    );
  }

  // Chevron navigation (name-sorted, same order as ObjectDetail table)
  const sortedInspections = [...allInspections].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const currentIndex   = sortedInspections.findIndex((i) => i.id === inspectionId);
  const prevInspection = currentIndex > 0 ? sortedInspections[currentIndex - 1] : null;
  const nextInspection = currentIndex < sortedInspections.length - 1 ? sortedInspections[currentIndex + 1] : null;
  const showChevrons   = sortedInspections.length > 1;

  const typeInfo       = INSPECTION_TYPE_INFO[inspection.type] || { label: inspection.name || inspection.type, category: inspection.type };
  const wizardConfig   = INSPECTION_WIZARD_CONFIG[inspection.type] || null;

  // Effective status is computed server-side (single source of truth).
  const effectiveStatus = inspection.effectiveStatus;

  // Delete handler — only reachable for types without a wizard config
  async function handleDelete() {
    const confirmed = window.confirm(`Delete "${typeInfo.label}"? This cannot be undone.`);
    if (!confirmed) return;
    await deleteInspection(inspectionId);
    navigate(`/client/${clientId}/object/${objectId}`);
  }

  // Per-inspection Print Report was removed in v3.5.6 — use the
  // object-level "Print Report" button on ObjectDetail, which can
  // build a single-inspection PDF by selecting just one inspection
  // in the picker. The combined endpoint handles both shapes.

  // ── Info card (shown inside header, below h1) ──────────────────
  function renderInfoCard() {
    const fd = inspection.formData || {};
    const ndtOperatorsStr = Array.isArray(fd.ndtOperators)
      ? fd.ndtOperators.map((op) => op.name || op).filter(Boolean).join(', ')
      : '';
    // Equipment display:
    //   • acceptsModule=yes + module attached → Name (Module)
    //   • acceptsModule=yes + no module       → Name (Missing module)
    //   • acceptsModule=no/na/unset           → Name
    // The module parenthetical is rendered with normal font-weight so it
    // doesn't inherit od-info-value's 700-weight; the equipment name keeps
    // the bold default.
    const equipmentModulesMap = fd.equipmentModules || {};
    const equipmentList = Array.isArray(fd.equipment)
      ? fd.equipment.filter(Boolean).map((name) => {
          if (!equipmentAcceptsModule(name)) return { name, suffix: null };
          const m = equipmentModulesMap[name];
          return { name, suffix: m || 'Missing UTC module' };
        })
      : [];
    const equipmentJsx = equipmentList.length === 0
      ? '—'
      : equipmentList.map((entry, i) => (
          <React.Fragment key={i}>
            {i > 0 && ', '}
            {entry.name}
            {entry.suffix && (
              <span style={{ fontWeight: 400 }}> ({entry.suffix})</span>
            )}
          </React.Fragment>
        ));
    const equipmentStr = equipmentList.map((e) => e.name + (e.suffix ? ` (${e.suffix})` : '')).join(', ');
    const hasSomeData =
      ndtOperatorsStr || fd.ndtReporter || equipmentStr ||
      fd.terraProcedure || fd.typeOfInspection || fd.insideOutside || fd.manualOrDrone;

    return (
      <div className="insp-info-card">
        <div className="insp-info-header">
          <div className="insp-info-title">Inspection Information</div>
          {wizardConfig && (
            <button className="btn btn-secondary btn-sm" onClick={() => setShowEditModal(true)}>
              {readOnly ? 'Show more' : 'Edit'}
            </button>
          )}
        </div>
        {hasSomeData ? (
          <>
            {/* Full-width rows: NDT Operators, NDT Reporter, Equipment Used */}
            <div className="insp-info-full-rows">
              <div className="od-info-cell">
                <span className="od-info-label">NDT Operators:</span>
                <span className="od-info-value">{ndtOperatorsStr || '—'}</span>
              </div>
              <div className="od-info-cell">
                <span className="od-info-label">NDT Reporter:</span>
                <span className="od-info-value">{fd.ndtReporter || '—'}</span>
              </div>
              <div className="od-info-cell">
                <span className="od-info-label">Equipment Used:</span>
                <span className="od-info-value">{equipmentJsx}</span>
              </div>
            </div>

            {/* 2-column grid: col 1 = Terra Procedure + Type of Inspection,
                              col 2 = Inside/Outside + Manual/Drone */}
            <div className="od-info-grid">
              <div className="od-info-col">
                <div className="od-info-cell">
                  <span className="od-info-label">Terra Procedure:</span>
                  <span className="od-info-value">{fd.terraProcedure || '—'}</span>
                </div>
                <div className="od-info-cell">
                  <span className="od-info-label">Type of Inspection:</span>
                  <span className="od-info-value">{fd.typeOfInspection || '—'}</span>
                </div>
              </div>
              <div className="od-info-col">
                <div className="od-info-cell">
                  <span className="od-info-label">Inside / Outside:</span>
                  <span className="od-info-value">{fd.insideOutside || '—'}</span>
                </div>
                <div className="od-info-cell">
                  <span className="od-info-label">Manual / Drone:</span>
                  <span className="od-info-value">{fd.manualOrDrone || '—'}</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className="insp-info-empty">
            No inspection data yet. Click <strong>Edit</strong> to enter details.
          </p>
        )}
      </div>
    );
  }

  // EditOrDisplay is defined outside the component (see below InspectionDetail)

  // ── Synopsis card (VT-OSI and VT-ISI only) ───────────────────
  function renderSynopsis() {
    if (!['VT-OSI', 'VT-ISI', 'VT'].includes(inspection.type)) return null;

    const fd = inspection.formData || {};
    const synopsis = fd.synopsis || {};
    const insideOutside = fd.insideOutside || '\u2014';
    const objectType = object?.type || '\u2014';
    const objectID = object?.objectTag || '\u2014';
    const testSite = client?.name || '\u2014';

    return (
      <div className="insp-synopsis-card">
        <div className="insp-section-title-row" onClick={() => setSynopsisOpen(!synopsisOpen)}>
          <div className="insp-synopsis-title">Visual Inspection Synopsis</div>
          <svg className={`insp-collapse-chevron ${synopsisOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </div>

        {synopsisOpen && (<>
        {/* Introduction */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Introduction</h3>
          <p className="insp-synopsis-text">
            A Visual Inspection was carried out by Terra Inspectioneering B.V. on the{' '}
            <span className="insp-synopsis-dynamic">{insideOutside}</span> of{' '}
            <span className="insp-synopsis-dynamic">{objectType}</span> -{' '}
            <span className="insp-synopsis-dynamic">{objectID}</span>.
          </p>
          <p className="insp-synopsis-text">
            This section presents the findings of the Visual Inspection. The inspection was performed in accordance
            with the applicable procedures valid at the date of inspection. These procedures are listed in the
            Visual Inspection Data Sheet and can be found under the Documents section in 3D Inspect.
          </p>
        </div>

        {/* Purpose of Investigation */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Purpose of Investigation</h3>
          <p className="insp-synopsis-text">
            The purpose of this inspection was to determine the integrity and condition of predefined parts of the
            equipment, as agreed with the asset owner, through Visual Inspection.
          </p>
        </div>

        {/* Scope of Inspection */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Scope of Inspection</h3>
          <p className="insp-synopsis-text">
            Visual Inspection provides an indication of the general condition of the inspected equipment. The
            inspection was carried out through indirect visual examination of the object using the equipment listed
            in the Visual Inspection Data Sheet.
          </p>
        </div>

        {/* Inspection Limitations */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Inspection Limitations</h3>
          <p className="insp-synopsis-text">
            Visual Inspection was limited to areas that were accessible and visible at the time of inspection. Areas
            that were obstructed, covered, submerged, or otherwise inaccessible could not be inspected.
          </p>
          <p className="insp-synopsis-text">
            Visual Inspection identifies visible surface conditions only and does not provide information regarding
            internal defects or subsurface conditions unless externally evident.
          </p>
          <p className="insp-synopsis-text">
            The findings presented in this report reflect the condition of the inspected areas at the time of
            inspection only.
          </p>
        </div>

        {/* Equipment used */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Equipment used</h3>
          <p className="insp-synopsis-text">
            The equipment used during the inspection are listed in the Visual Inspection Data Sheet.
          </p>
        </div>

        {/* Definitions */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Definitions</h3>
          <ul className="insp-synopsis-list">
            <li>
              <strong>Indication:</strong> A visible irregularity, damage, or condition observed during the
              inspection that may require further evaluation or monitoring according to the acceptance criteria.
            </li>
            <li>
              <strong>Observation:</strong> A recorded image intended to document the condition of the inspected area.
            </li>
          </ul>
        </div>

        {/* Orientation */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Orientation</h3>
          <p className="insp-synopsis-text">
            Videos, if recorded, follow the naming convention: &ldquo;Video &lt;#number&gt; -{' '}
            <span className="insp-synopsis-dynamic">{testSite}</span> -{' '}
            <span className="insp-synopsis-dynamic">{objectID}</span> -
            &lt;Inspected_area&gt; - &lt;original_video_name&gt;.mp4&rdquo;
          </p>
          <p className="insp-synopsis-text">
            Scanline 1 corresponds to True North, unless stated otherwise. Videos are recorded in a clockwise
            direction.
          </p>
        </div>

        {/* Inspected areas (editable, required) */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Inspected areas</h3>
          <EditOrDisplay
            displayValue={synopsis.inspectedAreas || ''}
            onSave={(val) => saveSynopsisField('inspectedAreas', val)}
            required
          />
        </div>

        {/* Remarks (editable, required) */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Remarks</h3>
          <EditOrDisplay
            displayValue={synopsis.remarks || ''}
            onSave={(val) => saveSynopsisField('remarks', val)}
            required
          />
        </div>

        {/* List of Annotations (editable, optional) */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">List of Annotations</h3>
          <EditOrDisplay
            displayValue={synopsis.annotations || ''}
            onSave={(val) => saveSynopsisField('annotations', val)}
            required={false}
            emptyLabel="None"
            leftAction={
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowRefImagesModal(true)}
                type="button"
              >
                Reference Images ({(synopsis.annotationReferenceIds || []).length})
              </button>
            }
          />
        </div>
        </>)}
      </div>
    );
  }

  // ── Magnetic Inspection Synopsis (MT-OSI / MT-ISI / MT) ────────
  // Mirrors VT's renderSynopsis but with the MT-specific prose. Only
  // the Remarks field is editable; everything else is static template
  // text that the report PDF picks up the same way.
  function renderMagneticSynopsis() {
    if (!['MT-OSI', 'MT-ISI', 'MT'].includes(inspection.type)) return null;

    const fd = inspection.formData || {};
    const synopsis = fd.synopsis || {};
    const insideOutside = fd.insideOutside || '—';
    const objectType = object?.type || '—';
    const objectID = object?.objectTag || '—';

    return (
      <div className="insp-synopsis-card">
        <div className="insp-section-title-row" onClick={() => setMagSynopsisOpen(!magSynopsisOpen)}>
          <div className="insp-synopsis-title">Magnetic Inspection Synopsis</div>
          <svg className={`insp-collapse-chevron ${magSynopsisOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </div>

        {magSynopsisOpen && (<>
        {/* Introduction */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Introduction</h3>
          <p className="insp-synopsis-text">
            A Magnetic Inspection was carried out by Terra Inspectioneering B.V. on the{' '}
            <span className="insp-synopsis-dynamic">{insideOutside}</span> of{' '}
            <span className="insp-synopsis-dynamic">{objectType}</span> -{' '}
            <span className="insp-synopsis-dynamic">{objectID}</span>.
          </p>
          <p className="insp-synopsis-text">
            This section presents the findings of the Magnetic Inspection. The inspection was performed in accordance
            with the applicable procedures valid at the date of inspection. These procedures are listed in the
            Magnetic Inspection Data Sheet and can be found under the Documents section in 3D Inspect.
          </p>
        </div>

        {/* Purpose of Investigation */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Purpose of Investigation</h3>
          <p className="insp-synopsis-text">
            The purpose of this inspection was to determine the integrity and condition of predefined parts of the
            equipment, as agreed with the asset owner, through Magnetic Inspection.
          </p>
        </div>

        {/* Inspection Limitations */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Inspection Limitations</h3>
          <p className="insp-synopsis-text">
            Magnetic Inspection was limited to areas that were accessible at the time of inspection.
          </p>
          <p className="insp-synopsis-text">
            Examination reliability is dependent on surface condition. Contaminants such as dirt, grease, or moisture
            may mask or interfere with relevant indications. Weld spatter and other irregularities may produce
            non-relevant indications, affecting the interpretation of the results.
          </p>
          <p className="insp-synopsis-text">
            Indications that cannot be clearly classified as relevant or non-relevant; if doubt remains after
            re-examination, they shall be considered relevant.
          </p>
          <p className="insp-synopsis-text">
            The findings presented in this report reflect the condition of the inspected areas at the time of
            inspection only.
          </p>
        </div>

        {/* Equipment used */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Equipment used</h3>
          <p className="insp-synopsis-text">
            The equipment used during the inspection are listed in the Magnetic Inspection Data Sheet.
          </p>
        </div>

        {/* Definitions */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Definitions</h3>
          <ul className="insp-synopsis-list">
            <li>
              <strong>Non-relevant Indication:</strong> Indications resulting from surface conditions, grooves,
              pitting, or material characteristics rather than actual imperfections.
            </li>
            <li>
              <strong>Relevant Indications:</strong> Indications exceeding 1.5 mm that are caused by an imperfection
              and require evaluation against the acceptance criteria.
            </li>
            <li>
              <strong>Linear indication:</strong> An indication whose length exceeds three times its width.
            </li>
            <li>
              <strong>Rounded indication:</strong> An indication with a circular or elliptical shape whose length is
              equal to or less than three times its width.
            </li>
          </ul>
        </div>

        {/* Remarks — editable field, required */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Remarks</h3>
          <EditOrDisplay
            displayValue={synopsis.remarks || ''}
            onSave={(val) => saveSynopsisField('remarks', val)}
            required
          />
        </div>

        {/* List of Annotations — editable, optional. Mirrors the VT
            synopsis: the textarea stores `synopsis.annotations` and the
            left-action button opens the shared ReferenceImagesModal,
            which writes `synopsis.annotationReferenceIds`. */}
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">List of Annotations</h3>
          <EditOrDisplay
            displayValue={synopsis.annotations || ''}
            onSave={(val) => saveSynopsisField('annotations', val)}
            required={false}
            emptyLabel="None"
            leftAction={
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowRefImagesModal(true)}
                type="button"
              >
                Reference Images ({(synopsis.annotationReferenceIds || []).length})
              </button>
            }
          />
        </div>
        </>)}
      </div>
    );
  }

  // ── Penetrant Inspection Synopsis (PT-OSI / PT-ISI / PT) ───────
  // Scaffold replicating renderMagneticSynopsis verbatim with the
  // labels swapped to "Penetrant". User will edit the prose later.
  function renderPenetrantSynopsis() {
    if (!['PT-OSI', 'PT-ISI', 'PT'].includes(inspection.type)) return null;

    const fd = inspection.formData || {};
    const synopsis = fd.synopsis || {};
    const insideOutside = fd.insideOutside || '—';
    const objectType = object?.type || '—';
    const objectID = object?.objectTag || '—';

    return (
      <div className="insp-synopsis-card">
        <div className="insp-section-title-row" onClick={() => setPtSynopsisOpen(!ptSynopsisOpen)}>
          <div className="insp-synopsis-title">Penetrant Inspection Synopsis</div>
          <svg className={`insp-collapse-chevron ${ptSynopsisOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </div>

        {ptSynopsisOpen && (<>
        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Introduction</h3>
          <p className="insp-synopsis-text">
            A Penetrant Inspection was carried out by Terra Inspectioneering B.V. on the{' '}
            <span className="insp-synopsis-dynamic">{insideOutside}</span> of{' '}
            <span className="insp-synopsis-dynamic">{objectType}</span> -{' '}
            <span className="insp-synopsis-dynamic">{objectID}</span>.
          </p>
          <p className="insp-synopsis-text">
            This section presents the findings of the Penetrant Inspection. The inspection was performed in accordance
            with the applicable procedures valid at the date of inspection. These procedures are listed in the
            Penetrant Inspection Data Sheet and can be found under the Documents section in 3D Inspect.
          </p>
        </div>

        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Purpose of Investigation</h3>
          <p className="insp-synopsis-text">
            The purpose of this inspection was to determine the integrity and condition of predefined parts of the
            equipment, as agreed with the asset owner, through Penetrant Inspection.
          </p>
        </div>

        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Inspection Limitations</h3>
          <p className="insp-synopsis-text">
            Penetrant Inspection was limited to areas that were accessible at the time of inspection.
          </p>
          <p className="insp-synopsis-text">
            Examination reliability is dependent on surface condition. Contaminants such as dirt, grease, or moisture
            may mask or interfere with relevant indications.
          </p>
          <p className="insp-synopsis-text">
            Indications that cannot be clearly classified as relevant or non-relevant; if doubt remains after
            re-examination, they shall be considered relevant.
          </p>
          <p className="insp-synopsis-text">
            The findings presented in this report reflect the condition of the inspected areas at the time of
            inspection only.
          </p>
        </div>

        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Equipment used</h3>
          <p className="insp-synopsis-text">
            The equipment used during the inspection are listed in the Penetrant Inspection Data Sheet.
          </p>
        </div>

        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Definitions</h3>
          <ul className="insp-synopsis-list">
            <li>
              <strong>Non-relevant Indication:</strong> Indications resulting from surface conditions, grooves,
              pitting, or material characteristics rather than actual imperfections.
            </li>
            <li>
              <strong>Relevant Indications:</strong> Indications exceeding 1.5 mm that are caused by an imperfection
              and require evaluation against the acceptance criteria.
            </li>
            <li>
              <strong>Linear indication:</strong> An indication whose length exceeds three times its width.
            </li>
            <li>
              <strong>Rounded indication:</strong> An indication with a circular or elliptical shape whose length is
              equal to or less than three times its width.
            </li>
          </ul>
        </div>

        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">Remarks</h3>
          <EditOrDisplay
            displayValue={synopsis.remarks || ''}
            onSave={(val) => saveSynopsisField('remarks', val)}
            required
          />
        </div>

        <div className="insp-synopsis-section">
          <h3 className="insp-synopsis-subheading">List of Annotations</h3>
          <EditOrDisplay
            displayValue={synopsis.annotations || ''}
            onSave={(val) => saveSynopsisField('annotations', val)}
            required={false}
            emptyLabel="None"
            leftAction={
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowRefImagesModal(true)}
                type="button"
              >
                Reference Images ({(synopsis.annotationReferenceIds || []).length})
              </button>
            }
          />
        </div>
        </>)}
      </div>
    );
  }

  // ── Inspection Captures card (all inspection types) ─────────────
  function renderCaptures() {
    async function handleAutofill() {
      const drivePath     = object?.googleDrivePath || '';
      const inspectionNum = object?.generalInfo?.inspectionNumber || '';
      const type          = inspection.type;
      const parts         = [drivePath, inspectionNum, '_tibInspectionStorage', type].filter(Boolean);
      const path = parts.join('\\');
      setCapturesDrivePath(path);
      await saveCapturesDrivePath(path);
    }

    async function handlePathBlur() {
      await saveCapturesDrivePath(capturesDrivePath);
    }

    // Compute composition options: respect chain relationship
    function getCompositionOptions() {
      const cfg = ddlRegConfigs['captures_inspectComposition'];
      if (cfg?.relationship === 'chain' && cfg.pointTo === 'captures_inspectObjectType' && capturesObjectType) {
        const parentItem = compositionFullItems.find((it) => it.label === capturesObjectType);
        return (parentItem?.children || []).map((it) => it.label);
      }
      return compositionFullItems.map((it) => it.label);
    }

    const compositionOpts = getCompositionOptions();

    async function handleObjectTypeChange(value) {
      setCapturesObjectType(value);
      setCapturesComposition('');
      await saveCapturesDropdowns({ capturesObjectType: value, capturesComposition: '' });
    }

    async function handleCompositionChange(value) {
      setCapturesComposition(value);
      await saveCapturesDropdowns({ capturesComposition: value });
    }

    // ── Sync handler — non-blocking: metadata → poll background job ─
    async function handleSync() {
      // Guard against rapid double-clicks firing the sync twice before React re-renders
      // the disabled state on the button.
      if (syncRunning) return;

      const presetCodes = IMAGE_CODES[capturesObjectType]?.[capturesComposition] ?? [];
      const isPreset = presetCodes.length > 0;

      // If resyncing, clear existing captures visually right away (mirrors Delete Sync UX)
      if (captures.length > 0) setCaptures([]);

      setSyncRunning(true);
      setSyncStatus({ phase: 'metadata', total: 0, loaded: 0, failed: 0 });
      setShowSyncModal(false);

      try {
        // Phase 1 — save metadata (fast, no image I/O)
        await syncCaptures(inspectionId, capturesDrivePath, presetCodes, isPreset);

        // Show gallery with "No image" placeholders immediately
        const fresh = await getCaptures(inspectionId);
        setCaptures(fresh);

        // Phase 2 — backend loads images in background; we poll
        startPolling(inspectionId);
      } catch (err) {
        setSyncStatus({ phase: 'error', total: 0, loaded: 0, failed: 0, error: err.message || 'Sync failed.' });
        setSyncRunning(false);
        setShowSyncModal(true);
      }
    }

    // Delete all captures for this inspection
    async function handleDeleteSync() {
      const confirmed = window.confirm('Delete all synced images for this inspection? This cannot be undone.');
      if (!confirmed) return;
      try {
        await deleteCaptures(inspectionId);
        setCaptures([]);
        setSyncStatus(null);
      } catch (err) {
        console.error('[TIB] Delete captures failed:', err);
      }
    }

    const capturesTitle = `${typeInfo.category} Captures`;
    const syncDisabled = syncRunning || !capturesDrivePath?.trim() || !capturesObjectType || !capturesComposition;

    // Button label driven by sync status polling
    let buttonLabel;
    if (syncRunning && syncStatus) {
      if (syncStatus.phase === 'images' && syncStatus.total > 0) {
        buttonLabel = `Extracting Images... (${syncStatus.loaded + syncStatus.failed}/${syncStatus.total})`;
      } else {
        buttonLabel = 'Retrieving information...';
      }
    } else {
      buttonLabel = captures.length > 0 ? 'Resync Images' : 'Sync Images';
    }

    // Progress bar percentage (for inline style)
    const progressPct = syncRunning && syncStatus?.total > 0
      ? ((syncStatus.loaded + syncStatus.failed) / syncStatus.total) * 100
      : 0;

    return (
      <div className="insp-captures-card">
        <div className="insp-section-title-row" onClick={() => setCapturesOpen(!capturesOpen)}>
          <div className="insp-captures-title">{capturesTitle}</div>
          <svg className={`insp-collapse-chevron ${capturesOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </div>

        {capturesOpen && (
          <>
            {/* Drive path input with Autofill button inside */}
            <label className="captures-dropdown-label">Google Drive path for inspection images</label>
            <div className="captures-path-wrapper">
              <input
                type="text"
                className="captures-path-input"
                value={capturesDrivePath}
                onChange={(e) => setCapturesDrivePath(e.target.value)}
                onBlur={handlePathBlur}
                placeholder="Enter or autofill Google Drive path..."
                disabled={readOnly}
              />
              {!readOnly && (
                <button
                  className="btn btn-secondary btn-sm captures-autofill-btn"
                  onClick={handleAutofill}
                  type="button"
                >
                  Autofill path
                </button>
              )}
            </div>

            {/* Dropdowns row */}
            <div className="captures-dropdowns-row">
              <div className="captures-dropdown-group">
                <label className="captures-dropdown-label">3D-Inspect Object Type</label>
                <select
                  className="captures-dropdown-select"
                  value={capturesObjectType}
                  onChange={(e) => handleObjectTypeChange(e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">— Select —</option>
                  {objectTypeOpts.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className="captures-dropdown-group">
                <label className="captures-dropdown-label">3D-Inspect Composition Breakdown</label>
                <select
                  className="captures-dropdown-select"
                  value={capturesComposition}
                  onChange={(e) => handleCompositionChange(e.target.value)}
                  disabled={!capturesObjectType || readOnly}
                >
                  <option value="">— Select —</option>
                  {compositionOpts.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sync / Resync + Delete buttons */}
            <div className="captures-sync-row">
              <button
                className={`btn btn-primary captures-sync-btn ${syncRunning ? 'syncing' : ''}`}
                onClick={handleSync}
                disabled={syncDisabled || readOnly}
                type="button"
              >
                <span className="captures-sync-btn-label">{buttonLabel}</span>
                {syncRunning && (
                  <span
                    className="captures-sync-progress"
                    style={{ width: `${progressPct}%` }}
                  />
                )}
              </button>
              {captures.length > 0 && !syncRunning && (
                <button
                  className="btn btn-danger captures-delete-btn"
                  onClick={handleDeleteSync}
                  type="button"
                  disabled={readOnly}
                >
                  Delete Sync
                </button>
              )}
            </div>

            {/* Last sync timestamp */}
            {captures.length > 0 && !syncRunning && captures[0]?.created_at && (
              <p className="captures-last-sync">
                Last synced: {new Date(captures[0].created_at).toLocaleString()}
              </p>
            )}

            {/* Gallery — rendered when captures exist in DB */}
            {captures.length > 0 && (
              <CapturesGallery captures={captures} />
            )}
          </>
        )}

        {/* Sync completion modal */}
        {showSyncModal && syncStatus && (
          <div className="insp-sync-modal-overlay" onClick={() => setShowSyncModal(false)}>
            <div className="insp-sync-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="insp-sync-modal-title">
                {syncStatus.error ? 'Sync Failed' : 'Sync Complete'}
              </h3>
              <div className="insp-sync-modal-body">
                {syncStatus.error ? (
                  <p>{syncStatus.error}</p>
                ) : (
                  <>
                    <p>{syncStatus.loaded} image(s) retrieved from Google Drive and uploaded to the database.</p>
                    {syncStatus.failed > 0 && (
                      <>
                        <p className="insp-sync-modal-warn">
                          {syncStatus.failed} / {syncStatus.total} image(s) could not be found.
                        </p>
                        {syncStatus.first_failed && (
                          <p style={{ fontSize: '0.75rem', color: '#888', wordBreak: 'break-all' }}>
                            Example: <strong>{syncStatus.first_failed}</strong>
                          </p>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="insp-sync-modal-actions">
                <button className="btn btn-primary" onClick={() => setShowSyncModal(false)}>OK</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Deformation Analysis card (DEFO only) ─────────────────────────
  // Mirrors the UT measurements card: one main card, an analysis sub-card
  // per added analysis. Each sub-card has a "Run Analysis" button (opens the
  // standalone analysis page) and shows its saved results below.
  function renderDeformationAnalysis() {
    if (inspection.type !== 'DEFO') return null;
    const reorderActive = !!defoReorder;

    return (
      <div className="insp-ut-card">
        <div className="insp-section-title-row" onClick={() => setDefoCardOpen(!defoCardOpen)}>
          <div className="insp-info-title">Deformation Analysis</div>
          <svg className={`insp-collapse-chevron ${defoCardOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>

        {defoCardOpen && (
          <div className="insp-ut-body">
            <div className="insp-ut-toolbar">
              {!readOnly && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowCreateDefoModal(true)}
                  disabled={reorderActive || defoAnalyses.length >= DEFO_ANALYSES.length}
                >
                  + Create Analysis
                </button>
              )}
            </div>

            {defoAnalyses.length === 0 && (
              <div className="insp-ut-empty">No analyses yet.</div>
            )}

            {defoAnalyses.length > 0 && (
              <div className={`insp-ut-subcards ${reorderActive ? 'reordering' : ''}`}>
                {defoAnalyses.map((a) => {
                  const key = `${a.kind}_${a.standard}`;
                  const meta = defoAnalysisByKey(key);
                  const title = meta?.title || key;
                  const isHeld = reorderActive && defoReorder.key === key;
                  return (
                    <div
                      key={key}
                      data-defo-subcard-id={key}
                      className={`insp-ut-subcard ${isHeld ? 'held' : ''}`}
                    >
                      <div
                        className="insp-ut-subcard-header"
                        onMouseDown={(e) => {
                          if (e.button !== 0) return;
                          if (!reorderActive && !readOnly) startDefoReorderHold(key);
                        }}
                        onMouseUp={cancelDefoReorderHold}
                        onMouseLeave={cancelDefoReorderHold}
                        onClick={() => {
                          if (reorderActive) return;
                          toggleDefoSubcardCollapse(key);
                        }}
                      >
                        <div className="insp-ut-subcard-heading-block">
                          <div className="insp-ut-subcard-heading">{title}</div>
                        </div>
                        <div className="insp-ut-subcard-actions">
                          <button
                            className="insp-ut-subcard-edit-btn"
                            onClick={(e) => { e.stopPropagation(); setDefoEditKey(key); }}
                            disabled={reorderActive}
                            aria-label="Edit analysis"
                          >
                            {readOnly ? 'Show more' : 'Edit'}
                          </button>
                          <svg
                            className={`insp-collapse-chevron ${!a.collapsed ? 'open' : ''}`}
                            viewBox="0 0 24 24" width="18" height="18"
                            fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </div>

                      {!a.collapsed && (
                        <div className="insp-ut-subcard-body">
                          {!readOnly && meta && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => window.open(
                                `/client/${clientId}/object/${objectId}/inspection/${inspectionId}/${meta.route}`,
                                '_blank',
                              )}
                              disabled={reorderActive}
                            >
                              {defoResultKinds.has(a.kind) ? 'Redo Analysis' : 'Run Analysis'}
                            </button>
                          )}
                          <DefoSubcards inspectionId={inspectionId} kind={a.kind} notes={a.notes || ''} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {reorderActive && (
              <div className="insp-ut-reorder-hint">
                Reorder mode — move the mouse to position the card, then release to drop.
              </div>
            )}
          </div>
        )}

        {/* Create Analysis modal */}
        {showCreateDefoModal && (
          <CreateDefoAnalysisModal
            existingKeys={defoAnalyses.map((a) => `${a.kind}_${a.standard}`)}
            onCancel={() => setShowCreateDefoModal(false)}
            onCreate={handleCreateDefoAnalyses}
          />
        )}

        {/* Edit Analysis — the measurement-table edit modal in "analysis"
            mode. Its Delete opens the confirmation below. */}
        {defoEditKey && (() => {
          const a = defoAnalyses.find((x) => `${x.kind}_${x.standard}` === defoEditKey);
          if (!a) return null;
          const title = defoAnalysisByKey(defoEditKey)?.title || defoEditKey;
          return (
            <SubcardEditModal
              variant="analysis"
              table={{
                id: defoEditKey,
                heading: title,
                examinedBy: a.examinedBy,
                manualOrDrone: a.manualOrDrone,
                referenceImageIds: a.referenceImageIds,
                notes: a.notes,
              }}
              inspection={inspection}
              inspectionId={inspectionId}
              onCancel={() => setDefoEditKey(null)}
              onSave={async (patch) => {
                await persistDefoAnalyses(defoAnalyses.map((x) => (
                  `${x.kind}_${x.standard}` === defoEditKey
                    ? {
                        ...x,
                        examinedBy: patch.examinedBy,
                        manualOrDrone: patch.manualOrDrone,
                        referenceImageIds: patch.referenceImageIds,
                        notes: patch.notes,
                      }
                    : x
                )));
                setDefoEditKey(null);
              }}
              onDelete={() => { setDefoEditKey(null); setDefoDeleteKey(defoEditKey); }}
            />
          );
        })()}

        {/* Delete-analysis confirmation (same modal shell as delete-table) */}
        {defoDeleteKey && (() => {
          const meta = defoAnalysisByKey(defoDeleteKey);
          const title = meta?.title || defoDeleteKey;
          return (
            <div className="modal-overlay" onClick={() => setDefoDeleteKey(null)}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
                <h2 className="modal-title">Delete analysis?</h2>
                <p className="modal-body-text">
                  Are you sure you want to delete <strong>{title}</strong>? This will
                  permanently remove its results. This action cannot be undone.
                </p>
                <div className="modal-actions" style={{ justifyContent: 'flex-end', gap: 'var(--space-sm)' }}>
                  <button className="btn btn-secondary" onClick={() => setDefoDeleteKey(null)}>
                    Cancel
                  </button>
                  <button className="btn btn-danger" onClick={() => handleDeleteDefoAnalysis(defoDeleteKey)}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  // ── Ultrasonic Inspection Measurements card (UT-OSI / UT-ISI only) ─
  function renderUTMeasurements() {
    if (!['UT-OSI', 'UT-ISI', 'UT'].includes(inspection.type)) return null;

    const reorderActive = !!utReorder;

    return (
      <div className="insp-ut-card">
        <div className="insp-section-title-row" onClick={() => setUtCardOpen(!utCardOpen)}>
          <div className="insp-info-title">Ultrasonic Inspection Measurements</div>
          <svg className={`insp-collapse-chevron ${utCardOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>

        {utCardOpen && (
          <div className="insp-ut-body">
            <div className="insp-ut-toolbar">
              {!readOnly && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowCreateUtModal(true)}
                  disabled={reorderActive}
                >
                  + Create Table
                </button>
              )}
              {utTables.length > 0 && (() => {
                // Auto-switch logic: when every card is in one state the
                // button flips to suggest the opposite. Middle cases hold
                // the last bulk-clicked mode.
                const allCollapsed = utTables.every((t) => !!t.collapsed);
                const allExpanded  = utTables.every((t) => !t.collapsed);
                const effectiveMode = allCollapsed
                  ? 'collapsed'
                  : allExpanded
                  ? 'expanded'
                  : utCollapseMode;
                const showExpandLabel = effectiveMode === 'collapsed';
                return (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => toggleUtCollapseAll(!showExpandLabel)}
                    disabled={reorderActive}
                  >
                    {showExpandLabel ? 'Expand all' : 'Collapse all'}
                  </button>
                );
              })()}
            </div>

            {utTables.length === 0 && (
              <div className="insp-ut-empty">No measurements yet.</div>
            )}

            {utTables.length > 0 && (
              <div className={`insp-ut-subcards ${reorderActive ? 'reordering' : ''}`}>
                {utTables.map((t) => {
                  const isHeld = reorderActive && utReorder.tableId === t.id;
                  return (
                    <div
                      key={t.id}
                      data-ut-subcard-id={t.id}
                      className={`insp-ut-subcard ${isHeld ? 'held' : ''}`}
                    >
                      <div
                        className="insp-ut-subcard-header"
                        onMouseDown={(e) => {
                          if (e.button !== 0) return;
                          if (!reorderActive) startReorderHold(t.id);
                        }}
                        onMouseUp={cancelReorderHold}
                        onMouseLeave={cancelReorderHold}
                        onClick={() => {
                          if (reorderActive) return;
                          toggleUtSubcardCollapse(t.id);
                        }}
                      >
                        <div className="insp-ut-subcard-heading-block">
                          <div className="insp-ut-subcard-heading">{t.heading}</div>
                          {(t.examinedBy || t.manualOrDrone) && (
                            <div className="insp-ut-subcard-meta">
                              {t.examinedBy && (
                                <span>Examined by: <strong>{t.examinedBy}</strong></span>
                              )}
                              {t.examinedBy && t.manualOrDrone && (
                                <span className="insp-ut-subcard-meta-sep">•</span>
                              )}
                              {t.manualOrDrone && (
                                <span>{t.manualOrDrone}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="insp-ut-subcard-actions">
                          <button
                            className="insp-ut-subcard-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setUtEditId(t.id);
                            }}
                            disabled={reorderActive}
                            aria-label={readOnly ? 'Show more' : 'Edit table'}
                          >
                            {readOnly ? 'Show more' : 'Edit'}
                          </button>
                          <svg
                            className={`insp-collapse-chevron ${!t.collapsed ? 'open' : ''}`}
                            viewBox="0 0 24 24" width="18" height="18"
                            fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </div>

                      {!t.collapsed && (
                        <div className="insp-ut-subcard-body">
                          {!readOnly && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => window.open(
                                `/client/${clientId}/object/${objectId}/inspection/${inspectionId}/ut-table/${t.id}`,
                                '_blank',
                              )}
                              disabled={reorderActive}
                            >
                              {hasMeasurements(t.sheets) ? 'Update Measurements' : 'Add Measurements'}
                            </button>
                          )}
                          <UTTablePreview sheets={t.sheets} />
                          {(() => {
                            // Auto-notes first, then any free-text notes the
                            // user typed in the Edit modal (one per non-blank
                            // line). De-dupes against the auto-notes so the
                            // user can't accidentally double up the canned text.
                            const auto = autoNotes(t.sheets);
                            const userLines = typeof t.notes === 'string'
                              ? t.notes.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
                              : [];
                            const seen = new Set(auto.map((n) => n.toLowerCase()));
                            const userNotes = userLines.filter((l) => {
                              const k = l.toLowerCase();
                              if (seen.has(k)) return false;
                              seen.add(k);
                              return true;
                            });
                            const all = [...auto, ...userNotes];
                            if (all.length === 0) return null;
                            return (
                              <div className="insp-ut-subcard-notes">
                                <div className="insp-ut-subcard-notes-title">Notes</div>
                                <ul className="insp-ut-subcard-notes-list">
                                  {all.map((n, i) => <li key={i}>{n}</li>)}
                                </ul>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {reorderActive && (
              <div className="insp-ut-reorder-hint">
                Reorder mode — move the mouse to position the card, then release to drop.
              </div>
            )}
          </div>
        )}

        {/* Create Table modal */}
        {showCreateUtModal && (
          <CreateUTTableModal
            inspectionType={inspection.type}
            existingTables={utTables}
            collections={utDdlCollections}
            regConfigs={ddlRegConfigs}
            onCancel={() => setShowCreateUtModal(false)}
            onCreate={handleCreateUtTables}
          />
        )}

        {/* Per-subcard Edit modal */}
        {utEditId && (() => {
          const target = utTables.find((t) => t.id === utEditId);
          if (!target) return null;
          return (
            <SubcardEditModal
              table={target}
              inspection={inspection}
              inspectionId={inspectionId}
              onCancel={() => setUtEditId(null)}
              onSave={async (patch) => {
                await handlePatchUtTable(utEditId, patch);
                setUtEditId(null);
              }}
              onDelete={() => {
                setUtEditId(null);
                setUtDeleteId(target.id);
              }}
            />
          );
        })()}

        {/* Delete-confirmation modal */}
        {utDeleteId && (() => {
          const target = utTables.find((t) => t.id === utDeleteId);
          if (!target) return null;
          return (
            <div className="modal-overlay" onClick={() => setUtDeleteId(null)}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
                <h2 className="modal-title">Delete table?</h2>
                <p className="modal-body-text">
                  Are you sure you want to delete <strong>{target.heading}</strong>? This will
                  permanently remove its measurements. This action cannot be undone.
                </p>
                <div className="modal-actions" style={{ justifyContent: 'flex-end', gap: 'var(--space-sm)' }}>
                  <button className="btn btn-secondary" onClick={() => setUtDeleteId(null)}>
                    Cancel
                  </button>
                  <button className="btn btn-danger" onClick={() => handleDeleteUtTable(utDeleteId)}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  // ── Corner-weld Inspection Measurements card (CW-OSI only) ──────
  // Minimal version of the UT measurements card. Single fixed sub-card
  // (auto-seeded by the load effect above), no Create / Reorder / Expand
  // bulk-action toolbar. Edit button opens the same SubcardEditModal,
  // Add/Update Measurements opens the UTTablePage spreadsheet.
  function renderCWMeasurements() {
    if (inspection.type !== 'CW-OSI') return null;
    return (
      <div className="insp-ut-card">
        <div className="insp-section-title-row" onClick={() => setUtCardOpen(!utCardOpen)}>
          <div className="insp-info-title">Corner-weld Inspection Measurements</div>
          <svg className={`insp-collapse-chevron ${utCardOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>

        {utCardOpen && utTables.length > 0 && (
          <div className="insp-ut-body">
            <div className="insp-ut-subcards">
              {utTables.map((t) => (
                <div
                  key={t.id}
                  data-ut-subcard-id={t.id}
                  className="insp-ut-subcard"
                >
                  <div
                    className="insp-ut-subcard-header"
                    onClick={() => toggleUtSubcardCollapse(t.id)}
                  >
                    <div className="insp-ut-subcard-heading-block">
                      <div className="insp-ut-subcard-heading">{t.heading}</div>
                      {(t.examinedBy || t.manualOrDrone) && (
                        <div className="insp-ut-subcard-meta">
                          {t.examinedBy && <span>Examined by: <strong>{t.examinedBy}</strong></span>}
                          {t.examinedBy && t.manualOrDrone && <span className="insp-ut-subcard-meta-sep">•</span>}
                          {t.manualOrDrone && <span>{t.manualOrDrone}</span>}
                        </div>
                      )}
                    </div>
                    <div className="insp-ut-subcard-actions">
                      <button
                        className="insp-ut-subcard-edit-btn"
                        onClick={(e) => { e.stopPropagation(); setUtEditId(t.id); }}
                        aria-label={readOnly ? 'Show more' : 'Edit table'}
                      >
                        {readOnly ? 'Show more' : 'Edit'}
                      </button>
                      <svg
                        className={`insp-collapse-chevron ${!t.collapsed ? 'open' : ''}`}
                        viewBox="0 0 24 24" width="18" height="18"
                        fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  </div>

                  {!t.collapsed && (
                    <div className="insp-ut-subcard-body">
                      {!readOnly && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => window.open(
                            `/client/${clientId}/object/${objectId}/inspection/${inspectionId}/ut-table/${t.id}`,
                            '_blank',
                          )}
                        >
                          {hasMeasurements(t.sheets) ? 'Update Measurements' : 'Add Measurements'}
                        </button>
                      )}
                      <UTTablePreview sheets={t.sheets} />
                      {/* Auto-notes mirror the UT card: mm unit + negative
                          degradation + R/L hints, deduped against any
                          user-typed notes from the Edit modal. */}
                      {(() => {
                        const auto = autoNotes(t.sheets);
                        const userLines = typeof t.notes === 'string'
                          ? t.notes.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
                          : [];
                        const seen = new Set(auto.map((n) => n.toLowerCase()));
                        const userNotes = userLines.filter((l) => {
                          const k = l.toLowerCase();
                          if (seen.has(k)) return false;
                          seen.add(k);
                          return true;
                        });
                        const all = [...auto, ...userNotes];
                        if (all.length === 0) return null;
                        return (
                          <div className="insp-ut-subcard-notes">
                            <div className="insp-ut-subcard-notes-title">Notes</div>
                            <ul className="insp-ut-subcard-notes-list">
                              {all.map((n, i) => <li key={i}>{n}</li>)}
                            </ul>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-subcard Edit modal (shared with UT) */}
        {utEditId && (() => {
          const target = utTables.find((t) => t.id === utEditId);
          if (!target) return null;
          return (
            <SubcardEditModal
              table={target}
              inspection={inspection}
              inspectionId={inspectionId}
              onCancel={() => setUtEditId(null)}
              onSave={async (patch) => {
                await handlePatchUtTable(utEditId, patch);
                setUtEditId(null);
              }}
              // CW-OSI sub-card is fixed — Delete is suppressed (not passed).
            />
          );
        })()}
      </div>
    );
  }

  // ── Incomplete alert helper ─────────────────────────────────────
  function renderIncompleteAlert(missingItems) {
    if (!missingItems || missingItems.length === 0) return null;
    return (
      <div className="insp-incomplete-alert">
        <span className="insp-incomplete-icon">&#9888;</span>
        Incomplete &mdash; {missingItems.join(', ')}
      </div>
    );
  }

  function getInfoCardMissing() {
    const fd = inspection.formData || {};
    const missing = [];
    const isUT = inspection.type === 'UT-OSI' || inspection.type === 'UT-ISI' || inspection.type === 'UT';

    // Procedure
    if (!fd.specificCode)       missing.push('Specific Code');
    if (!fd.terraProcedure)     missing.push('Terra Procedure');
    if (!fd.acceptanceStandard) missing.push('Acceptance Standard');

    // Examination
    if (!fd.subject)          missing.push('Subject');
    if (!fd.technique)        missing.push('Technique');
    if (!fd.typeOfInspection) missing.push('Type of Inspection');
    if (!fd.extentOfTesting)  missing.push('Extent of Testing');
    if (!fd.surfaceCondition) missing.push('Surface Condition');
    if (!fd.surfaceTemp)      missing.push('Surface Temperature (°C)');
    if (!fd.insideOutside)    missing.push('Inside / Outside');
    if (!fd.manualOrDrone)    missing.push('Manual / Drone');
    if (isUT && !fd.couplant) missing.push('Couplant');

    // Equipment
    const insTypeUpper = (inspection.type || '').toUpperCase();
    const isMT = ['MT-OSI', 'MT-ISI', 'MT'].includes(insTypeUpper);
    const isPT = ['PT-OSI', 'PT-ISI', 'PT'].includes(insTypeUpper);
    if (!Array.isArray(fd.equipment) || fd.equipment.filter(Boolean).length === 0) missing.push('Equipment');

    // MT / PT category pickers — surface each blank category here so
    // the banner under the inspection-info card lists exactly which
    // categories are missing instead of just "Equipment". Keep these
    // labels in sync with MT_EQUIPMENT_CATEGORIES / PT_EQUIPMENT_CATEGORIES
    // in InspectionWizardPage.js.
    if (isMT) {
      const picks = fd.mtEquipmentPicks || {};
      const cats = [
        ['magneticYoke',   'Magnetic Yoke'],
        ['penetrameter',   'Penetrameter'],
        ['contrastPaint',  'Contrast Paint'],
        ['testSuspension', 'Test Suspension'],
        ['luxMeter',       'Lux Meter'],
      ];
      for (const [k, lbl] of cats) {
        if (!picks[k]) missing.push(lbl);
      }
    }
    if (isPT) {
      const picks = fd.ptEquipmentPicks || {};
      const cats = [
        ['penetrantLiquid',  'Penetrant Liquid'],
        ['penetrantRemover', 'Penetrant Remover'],
        ['developer',        'Developer'],
        ['luxMeter',         'Lux Meter'],
      ];
      for (const [k, lbl] of cats) {
        if (!picks[k]) missing.push(lbl);
      }
    }

    // UT-only: Probes + Stepwedges + missing modules for module-eligible
    // equipment (acceptsModule = 'yes' on the equipment but no module attached
    // in this inspection).
    if (isUT) {
      if (!Array.isArray(fd.probes)     || fd.probes.filter(Boolean).length === 0)     missing.push('Probe Modules');
      if (!Array.isArray(fd.stepwedges) || fd.stepwedges.filter(Boolean).length === 0) missing.push('Stepwedges');

      const eqModules = fd.equipmentModules || {};
      const missingModuleEqs = (Array.isArray(fd.equipment) ? fd.equipment : [])
        .filter((name) => equipmentAcceptsModule(name) && !eqModules[name]);
      for (const name of missingModuleEqs) {
        missing.push(`Module for ${name}`);
      }
    }

    // NDT Operators + Reporter
    if (!Array.isArray(fd.ndtOperators) || fd.ndtOperators.length === 0) missing.push('NDT Operators');
    if (!fd.ndtReporter) missing.push('NDT Reporter');

    return missing;
  }

  function getSynopsisMissing() {
    const synopsis = inspection.formData?.synopsis || {};
    const missing = [];
    if (!synopsis.inspectedAreas?.trim()) missing.push('Inspected Areas');
    if (!synopsis.remarks?.trim()) missing.push('Remarks');
    return missing;
  }

  // Magnetic Inspection Synopsis — only Remarks is editable / required.
  // The template prose (Introduction, Purpose, Limitations, Equipment,
  // Definitions) is fixed.
  function getMagneticSynopsisMissing() {
    const synopsis = inspection.formData?.synopsis || {};
    const missing = [];
    if (!synopsis.remarks?.trim()) missing.push('Remarks');
    return missing;
  }

  // PT scaffold — same required-field rule as MT.
  function getPenetrantSynopsisMissing() {
    const synopsis = inspection.formData?.synopsis || {};
    const missing = [];
    if (!synopsis.remarks?.trim()) missing.push('Remarks');
    return missing;
  }

  function getCapturesMissing() {
    const missing = [];
    if (!capturesDrivePath?.trim()) missing.push('Drive Path');
    if (!capturesObjectType) missing.push('Object Type');
    if (!capturesComposition) missing.push('Composition');
    return missing;
  }

  const readOnly = !!object?.archivedAt;   // archived → read-only mirror

  return (
    <ReadOnlyContext.Provider value={readOnly}>
    <div className="client-detail">
      <button className="btn-back" onClick={() => navigate(`/client/${clientId}/object/${objectId}`)}>
        &larr; Back to {object.objectTag || object.name || 'Object'}
      </button>

      <header className="detail-header">
        <div className="detail-header-top">
          <div className="insp-heading-row">
            <h1>{typeInfo.label}</h1>
            <span className="insp-status-badge" data-status={effectiveStatus}>{effectiveStatus}</span>
          </div>
          <div className="header-actions">
            {showChevrons && (
              <div className="nav-chevrons">
                {prevInspection ? (
                  <button
                    className="nav-chevron"
                    onClick={() => navigate(`/client/${clientId}/object/${objectId}/inspection/${prevInspection.id}`)}
                    title={prevInspection.type}
                  >
                    &lsaquo;
                  </button>
                ) : <span className="nav-chevron-hidden">&lsaquo;</span>}
                {nextInspection ? (
                  <button
                    className="nav-chevron"
                    onClick={() => navigate(`/client/${clientId}/object/${objectId}/inspection/${nextInspection.id}`)}
                    title={nextInspection.type}
                  >
                    &rsaquo;
                  </button>
                ) : <span className="nav-chevron-hidden">&rsaquo;</span>}
              </div>
            )}

            {/* Per-inspection Print Report removed in v3.5.6 — handled by
                the object-level Print Report button on ObjectDetail.
                Non-wizard types still get a Delete action here. */}
            {!wizardConfig && !readOnly && (
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                Delete
              </button>
            )}
          </div>
        </div>

        {/* Info card sits inside header (12px gap from h1, matching object page) */}
        {wizardConfig && renderInfoCard()}
        {wizardConfig && renderIncompleteAlert(getInfoCardMissing())}
      </header>

      {/* Synopsis section (VT-OSI and VT-ISI only) */}
      {renderSynopsis()}
      {['VT-OSI', 'VT-ISI', 'VT'].includes(inspection.type) && renderIncompleteAlert(getSynopsisMissing())}
      {renderMagneticSynopsis()}
      {['MT-OSI', 'MT-ISI', 'MT'].includes(inspection.type) && renderIncompleteAlert(getMagneticSynopsisMissing())}
      {renderPenetrantSynopsis()}
      {['PT-OSI', 'PT-ISI', 'PT'].includes(inspection.type) && renderIncompleteAlert(getPenetrantSynopsisMissing())}

      {/* Captures section (VT and MT types only) */}
      {CAPTURING_TYPES.includes(inspection.type) && renderCaptures()}
      {CAPTURING_TYPES.includes(inspection.type) && renderIncompleteAlert(getCapturesMissing())}

      {/* Ultrasonic Inspection Measurements (UT-OSI and UT-ISI only) */}
      {renderUTMeasurements()}

      {/* Corner-weld Inspection Measurements (CW-OSI only) */}
      {renderCWMeasurements()}

      {/* Deformation Analysis (DEFO only) — one card, analysis sub-cards */}
      {renderDeformationAnalysis()}

      {/* Appendix section (all inspection types) */}
      <AppendixCard
        inspectionId={inspectionId}
        inspectionType={inspection.type}
        referencedBy={appendixReferences(inspection, utTables)}
        onImageDeleted={(deletedId) => {
          // Backend has scrubbed the dead ID out of
          //   inspection.formData.synopsis.annotationReferenceIds
          //   inspection.formData.defoAnalyses[].referenceImageIds
          //   ut_data.tables[].referenceImageIds
          // Pull them back so the "Reference Images (N)" badges (in
          // the synopsis card AND per-sub-card edit modal) refresh
          // without waiting for the 15s poll. The deformation list is
          // local state (loaded once), so drop the id there too — else
          // Edit Analysis shows a stale count, and the next persist of the
          // list would write the dead id back.
          if (deletedId != null) {
            const dead = String(deletedId);
            setDefoAnalyses((cur) => cur.map((a) => (
              (a.referenceImageIds || []).some((id) => String(id) === dead)
                ? { ...a, referenceImageIds: a.referenceImageIds.filter((id) => String(id) !== dead) }
                : a
            )));
          }
          refreshInspection();
          getUTData(inspectionId)
            .then((res) => {
              const d = res?.ut_data;
              if (d && Array.isArray(d.tables)) setUtTables(d.tables);
            })
            .catch(() => {});
        }}
      />

      {/* Reference Images modal */}
      {showRefImagesModal && (
        <ReferenceImagesModal
          inspectionId={inspectionId}
          initialSelected={inspection.formData?.synopsis?.annotationReferenceIds || []}
          onCancel={() => setShowRefImagesModal(false)}
          onSave={async (ids) => {
            await saveSynopsisField('annotationReferenceIds', ids);
            setShowRefImagesModal(false);
          }}
        />
      )}

      {/* Edit wizard modal */}
      {showEditModal && wizardConfig && (
        <InspectionWizardPage
          inspection={inspection}
          inspectionId={inspectionId}
          clientId={clientId}
          objectId={objectId}
          onClose={() => { setShowEditModal(false); refreshInspection(); }}
          typeLabel={typeInfo.label}
          objectTag={object?.objectTag}
          typePrefix={wizardConfig.typePrefix}
          ndtOperatorCollection={wizardConfig.ndtOperatorCollection}
          equipmentCategory={wizardConfig.equipmentCategory}
          defaults={wizardConfig.defaults}
          objectStartDate={object?.generalInfo?.startDate}
          effectiveStatus={inspection.effectiveStatus}
        />
      )}
    </div>
    </ReadOnlyContext.Provider>
  );
}
